<template>
    <div class="request-create-page requests-glass argus-glass-page">
        <!-- Заголовок с навигацией -->
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <b-button variant="outline-secondary" size="sm" @click="goBackToList" class="mr-2">
                    <i class="fas fa-arrow-left"></i> Назад
                </b-button>
                <h4 class="d-inline-block mb-0">
                    <template v-if="loadingSourceRequest">
                        <b-spinner small class="mr-2"></b-spinner>
                        Загрузка данных...
                    </template>
                    <template v-else-if="linkedRequestContextLoaded">
                        Создание связанной заявки
                    </template>
                    <template v-else-if="sourceRequestLoaded">
                        Создание заявки (копия)
                    </template>
                    <template v-else>
                        Создание заявки
                    </template>
                </h4>
            </div>
        </div>

        <b-card class="request-glass-card">
            <b-form>
                <!-- Выбор типа заявки -->
                <div class="mb-4">
                    <div
                        v-if="form.request_type_id && selectedRequestType"
                        class="request-picker-summary border rounded bg-light mb-3"
                    >
                        <button
                            type="button"
                            class="request-picker-summary__button text-left"
                            :disabled="isLinkedRequestFlow"
                            @click="toggleRequestTypePicker"
                        >
                            <div class="d-flex justify-content-between align-items-center flex-wrap">
                                <div class="pr-3">
                                    <div class="small text-muted mb-1">Выбрана заявка</div>
                                    <div class="font-weight-bold">{{ selectedRequestCategoryOption ? selectedRequestCategoryOption.text : 'Категория не выбрана' }}</div>
                                    <div class="small text-muted mt-1">{{ selectedRequestType.text }}</div>
                                </div>
                                <div
                                    v-if="!isLinkedRequestFlow"
                                    class="d-flex align-items-center mt-2 mt-sm-0"
                                >
                                    <span class="small text-primary font-weight-bold mr-2">
                                        {{ requestTypePickerExpanded ? 'Скрыть' : 'Изменить' }}
                                    </span>
                                    <i
                                        class="fas"
                                        :class="requestTypePickerExpanded ? 'fa-chevron-up text-primary' : 'fa-chevron-down text-primary'"
                                    ></i>
                                </div>
                            </div>
                        </button>
                    </div>

                    <b-collapse :visible="requestTypePickerExpanded">
                        <div class="request-type-search mb-3">
                            <b-input-group>
                                <b-input-group-prepend>
                                    <span class="input-group-text">
                                        <i class="fas fa-search"></i>
                                    </span>
                                </b-input-group-prepend>
                                <b-form-input
                                    v-model.trim="requestTypeSearch"
                                    type="search"
                                    placeholder="Поиск по типам заявок"
                                    autocomplete="off"
                                ></b-form-input>
                                <b-input-group-append v-if="requestTypeSearch">
                                    <b-button
                                        type="button"
                                        variant="outline-secondary"
                                        @click="clearRequestTypeSearch"
                                    >
                                        <i class="fas fa-times"></i>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </div>

                        <div
                            v-if="!requestTypeSearchQuery && selectedRequestCategory && requestCategoryStepCollapsed"
                            class="request-step-summary d-sm-none border rounded bg-light mb-3"
                        >
                            <div class="request-step-summary__content">
                                <div class="small text-muted mb-1">Куда обращаемся</div>
                                <div class="font-weight-bold">
                                    {{ selectedRequestCategoryOption ? selectedRequestCategoryOption.text : 'Категория не выбрана' }}
                                </div>
                            </div>
                            <b-button
                                type="button"
                                variant="link"
                                size="sm"
                                class="request-step-summary__action"
                                @click="expandRequestCategoryStep"
                            >
                                Изменить
                            </b-button>
                        </div>

                        <div
                            v-if="!requestTypeSearchQuery"
                            class="mb-4 request-category-step request-picker-section request-picker-section--category"
                            :class="{ 'request-category-step--collapsed': selectedRequestCategory && requestCategoryStepCollapsed }"
                        >
                            <div class="request-picker-section__header">
                                <h5 class="mb-1">1. Куда обращаемся?</h5>
                                <div class="text-muted small">Выберите отдел, который должен обработать заявку.</div>
                            </div>

                            <div class="request-choice-grid">
                                <button
                                    v-for="category in visibleRequestCategories"
                                    :key="category.value"
                                    type="button"
                                    class="request-choice-card request-choice-card--category text-left"
                                    :class="categoryCardClass(category.value)"
                                    @click="selectRequestCategory(category.value)"
                                >
                                    <div class="request-choice-card__layout">
                                        <i :class="[category.icon, 'request-choice-icon', 'mr-3', selectedRequestCategory === category.value ? 'text-primary' : 'text-secondary']"></i>
                                        <div class="request-choice-card__text">
                                            <div class="font-weight-bold request-choice-title">{{ category.text }}</div>
                                            <div class="small text-muted mt-1 request-choice-description">{{ category.description }}</div>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            <b-alert
                                v-if="requestTypesLoaded && visibleRequestCategories.length === 0"
                                variant="warning"
                                show
                                class="mt-3 mb-0"
                            >
                                Нет доступных активных типов заявок.
                            </b-alert>
                        </div>

                        <div
                            v-if="selectedRequestCategory || requestTypeSearchQuery"
                            ref="requestTypeStep"
                            class="mb-1 request-type-step request-picker-section request-picker-section--type"
                        >
                            <div class="request-picker-section__header">
                                <h5 class="mb-1">2. Что нужно?</h5>
                                <div class="text-muted small">
                                    {{ requestTypeSearchQuery ? 'Выберите тип заявки из результатов поиска.' : 'Выберите конкретный тип заявки.' }}
                                </div>
                            </div>

                            <div class="request-choice-grid request-choice-grid--types">
                                <button
                                    v-for="type in requestTypePickerResults"
                                    :key="type.value"
                                    type="button"
                                    class="request-choice-card request-choice-card--type text-left"
                                    :class="typeCardClass(type.value)"
                                    @click="selectRequestType(type)"
                                >
                                    <div class="request-choice-card__layout">
                                        <i :class="[type.icon, 'request-choice-icon', 'mr-3', form.request_type_id === type.value ? 'text-primary' : 'text-secondary']"></i>
                                        <div class="request-choice-card__text">
                                            <div class="font-weight-bold request-choice-title">{{ type.text }}</div>
                                            <div
                                                v-if="type.description"
                                                class="small text-muted mt-1 request-choice-description"
                                                :title="type.description"
                                            >
                                                {{ getTruncatedRequestTypeDescription(type.description) }}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            <b-alert
                                v-if="requestTypesLoaded && requestTypePickerResults.length === 0"
                                variant="warning"
                                show
                                class="mt-3 mb-0"
                            >
                                {{ requestTypeSearchQuery ? 'По поиску ничего не найдено.' : 'По выбранному направлению ничего не найдено.' }}
                            </b-alert>

                            <b-form-invalid-feedback :state="form.request_type_id !== null" class="d-block">
                                Выберите тип заявки
                            </b-form-invalid-feedback>
                        </div>
                    </b-collapse>
                </div>

                <!-- Показываем остальные поля только после выбора типа заявки -->
                <div v-if="form.request_type_id">
                    <b-alert
                        v-if="linkedSourceRequest"
                        variant="light"
                        show
                        class="border mb-3"
                    >
                        <div class="small text-muted mb-1">{{ linkedSourceLabel }}</div>
                        <router-link
                            :to="linkedSourceRoute"
                            class="font-weight-bold"
                        >
                            {{ linkedSourceDisplayNumber }}
                        </router-link>
                        <span v-if="linkedSourceTitle" class="ml-1">{{ linkedSourceTitle }}</span>
                        <div
                            v-if="linkedSourceSummaryText"
                            class="small text-muted mt-1"
                        >
                            {{ linkedSourceSummaryText }}
                        </div>
                    </b-alert>

                    <b-alert
                        v-if="selectedRequestType && selectedRequestType.description && selectedRequestType.description.trim()"
                        variant="info"
                        show
                        class="mb-3"
                    >
                        <div class="font-weight-bold mb-1">Описание заявки</div>
                        <div class="mb-0" style="white-space: pre-line;">{{ selectedRequestType.description }}</div>
                    </b-alert>

                    <div class="row">
                        <div class="col-md-6">
                            <b-form-group label="Приоритет" label-for="priority">
                                <b-form-select
                                    id="priority"
                                    v-model="form.priority"
                                    :options="priorityOptions"
                                />
                            </b-form-group>
                        </div>
                        <div class="col-md-6">
                            <RequestDeadlineInput v-if="!form.recurrence" v-model="form.deadline" id="create-deadline" />
                            <p v-if="!form.recurrence && form.deadline && deadlinePolicyResult.code" class="small text-danger" role="alert">{{ deadlinePolicyResult.message }}</p>

                        </div>
                    </div>

                    <div v-if="recurrenceAvailable" class="mb-3">
                        <b-form-checkbox :checked="Boolean(form.recurrence)" :disabled="Boolean($route.query.recurrenceSeries)" switch @change="toggleRecurrence">Регулярная (повторяющаяся) заявка</b-form-checkbox>
                        <p v-if="form.recurrence" class="small text-muted mt-2">Создаётся серия. Каждое исполнение будет отдельной заявкой.</p>
                        <RequestRecurrenceSchedule v-if="form.recurrence" :value="form.recurrence.schedule" @input="setRecurrenceSchedule" />
                        <p v-if="form.recurrence && recurrenceInheritedFiles.length" class="small text-muted">Общие документы из предыдущей редакции: {{ recurrenceInheritedFiles.map(file => file.file_name).join(', ') }}. Они будут перенесены автоматически.</p>
                    </div>
                    <b-form-group 
                        v-if="selectedRequestType && selectedRequestType.requires_title"
                        label="Название заявки *" 
                        label-for="title"
                    >
                        <b-form-input
                            id="title"
                            v-model="form.title"
                            placeholder="Введите название заявки"
                            required
                            :state="form.title.trim() !== ''"
                        />
                        <b-form-invalid-feedback :state="form.title.trim() !== ''">
                            Введите название заявки
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <div v-if="showsBuildingProcess" class="row">
                        <div class="col-md-6">
                            <b-form-group :label="buildingProcessFieldLabel" label-for="building-process">
                                <v-select
                                    id="building-process"
                                    v-model="form.building_process_id"
                                    :options="buildingProcesses"
                                    label="text"
                                    :reduce="option => option.value"
                                    :placeholder="buildingProcessFieldPlaceholder"
                                    :filterable="true"
                                    :searchable="true"
                                    :clearable="!requiresBuildingProcess"
                                    :disabled="buildingProcessesLoading"
                                    :state="requiresBuildingProcess ? (form.building_process_id !== null ? null : false) : null"
                                    @input="onBuildingProcessChange"
                                />
                                <b-form-invalid-feedback :state="!requiresBuildingProcess || form.building_process_id !== null">
                                    {{ buildingProcessInvalidFeedback }}
                                </b-form-invalid-feedback>
                                <small v-if="isMechanicAssetCatalogOnObject" class="text-muted d-block mt-1">
                                    Используется как объект-источник для выбора техники. Объект назначения укажет исполнитель.
                                </small>
                                <small v-else-if="!requiresBuildingProcess" class="text-muted d-block mt-1">
                                    Рекомендуется выбрать, если заявка относится к конкретному объекту.
                                </small>
                            </b-form-group>
                        </div>
                    </div>

                    <CustomFieldsForm
                        v-if="hasCustomFields"
                        v-model="form.custom_fields"
                        :fields="customFieldDefinitions"
                        :request-type-id="form.request_type_id"
                        title="Дополнительные данные"
                        id-prefix="request-create-custom-field"
                    />

                    <!-- Для заявок со справочником позиций описание показываем после блока позиций -->
                    <ReportAnnulmentEditor v-if="isReportAnnulment" v-model="form.report_annulment.reports" :type-id="form.request_type_id" :process-id="form.building_process_id" />
                    <template v-if="!hasItemCatalog && showDescriptionField">
                        <RequestDetailsPrerequisites
                            v-if="!canFillRequestDetails"
                            :items="requestDetailsPrerequisites"
                            :title="requestDetailsPrerequisitesTitle"
                            :description="requestDetailsPrerequisitesDescription"
                        />

                        <b-form-group
                            v-else
                            :label="descriptionFieldLabel"
                            label-for="description"
                        >
                            <DiscussionEditor
                                id="description"
                                field="description" :context="{type: 'request', id: 'new'}"
                                :mention-state="form.description_mention_state" @mention-state="form.description_mention_state = $event"
                                v-model="form.description"
                                placeholder="Опишите суть заявки (Ctrl+V для вставки файлов из буфера)"
                                :required="isDescriptionRequired"
                                :state="descriptionFieldState"
                                @paste="onPasteToDescription"
                            />
                            <b-form-invalid-feedback :state="descriptionFieldState">
                                Введите описание заявки
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </template>

                    <!-- Поле для обоснования срочности -->
                    <div v-if="showUrgencyComment" class="border p-3 mb-3 bg-warning bg-opacity-10">
                        <h6 class="text-warning mb-3">
                            <i class="fas fa-exclamation-triangle"></i> Обоснование срочности
                        </h6>
                        <b-form-group label="Причина срочности *" label-for="urgency-comment">
                            <AppTextarea
                                id="urgency-comment"
                                v-model="form.urgency_comment"
                                placeholder="Укажите причину, по которой заявка требует срочного выполнения..."
                                required
                                :state="form.urgency_comment.trim() !== ''"
                            />
                            <b-form-invalid-feedback :state="form.urgency_comment.trim() !== ''">
                                Укажите причину срочности
                            </b-form-invalid-feedback>
                            <b-form-text class="text-muted">
                                <i class="fas fa-info-circle"></i> 
                                {{ urgencyReasonText }}
                            </b-form-text>
                            <b-alert v-if="isDeadlineViolated && selectedRequestType && selectedRequestType.requires_justification_if_late && selectedRequestType.late_approval_config && selectedRequestType.late_approval_config.enabled" 
                                    variant="info" show class="mt-2">
                                <i class="fas fa-user-tie"></i>
                                <strong>Внимание:</strong> Заявка будет автоматически направлена на дополнительное согласование из-за нарушения регламентных сроков.
                                <div class="mt-1">
                                    Дополнительно будет согласовывать:
                                    <span class="text-danger font-weight-bold">{{ lateApproversText || 'не удалось определить согласующих по настройкам типа заявки' }}</span>.
                                </div>
                            </b-alert>
                        </b-form-group>
                    </div>

                <!-- Специальные поля для заявок со справочником позиций -->
                <div v-if="hasItemCatalog" class="border p-3 mb-3 bg-light">
                    <h6 class="text-primary mb-3">
                        <i :class="selectedRequestType ? selectedRequestType.icon : 'fas fa-boxes'"></i> {{ itemCatalogBlockTitle }}
                    </h6>

                    <RequestDetailsPrerequisites
                        v-if="!canFillRequestDetails"
                        :items="requestDetailsPrerequisites"
                        :title="requestDetailsPrerequisitesTitle"
                        :description="requestDetailsPrerequisitesDescription"
                    />

                    <template v-else>
                        <!-- Выбор позиций из справочника -->
                        <b-form-group :label="`${itemCatalogLabel} *`" label-for="materials">
                            <div class="border rounded p-3">
                                <template v-if="isFuelCardIssueToRecipient">
                                    <b-form-group label="Получатель карты *" label-for="fuel-card-recipient" class="mb-2">
                                        <v-select
                                            id="fuel-card-recipient"
                                            v-model="fuelCardRecipientUserId"
                                            :options="fuelCardRecipientOptions"
                                            label="text"
                                            :reduce="option => option.value"
                                            :clearable="false"
                                            placeholder="Выберите сотрудника"
                                        />
                                        <EmployeeSelectionPreview :user-id="fuelCardRecipientUserId" :options="fuelCardRecipientOptions" />
                                    </b-form-group>
                                    <small class="text-muted d-block">
                                        Конкретную топливную карту выберет исполнитель в карточке заявки перед выдачей.
                                    </small>
                                    <div v-if="fuelCardCanEditLimits && form.selectedMaterials.length" class="mt-3">
                                        <div
                                            v-for="(material, index) in form.selectedMaterials"
                                            :key="`fuel-limit-issue-${material.id || material.catalog_item_id || index}`"
                                            class="border rounded p-2 mb-2"
                                        >
                                            <div class="font-weight-bold mb-1">
                                                {{ material.name || material.item_name || 'Топливная карта' }}
                                            </div>
                                            <div
                                                v-for="(limit, limitIndex) in material.fuel_limits"
                                                :key="`fuel-limit-issue-row-${limitIndex}`"
                                                class="d-flex align-items-center mb-2"
                                            >
                                                <b-form-select
                                                    v-model.number="limit.fuel_type_id"
                                                    :options="fuelLimitTypeOptions"
                                                    size="sm"
                                                    class="mr-2"
                                                    data-fuel-limit-type
                                                    :data-fuel-limit-material-index="index"
                                                    :data-fuel-limit-index="limitIndex"
                                                    @change="setFuelLimitType(limit, $event)"
                                                />
                                                <b-form-input
                                                    v-model.number="limit.value"
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    size="sm"
                                                    placeholder="Лимит в сутки"
                                                    class="mr-2"
                                                    data-fuel-limit-value
                                                    :data-fuel-limit-material-index="index"
                                                    :data-fuel-limit-index="limitIndex"
                                                    @input="setFuelLimitValue(limit, $event)"
                                                    @change="setFuelLimitValue(limit, $event)"
                                                />
                                                <b-button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    title="Удалить лимит"
                                                    @click="removeFuelLimitRow(material, limitIndex)"
                                                >
                                                    <i class="fas fa-times"></i>
                                                </b-button>
                                            </div>
                                            <b-button size="sm" variant="outline-primary" @click="addFuelLimitRow(material)">
                                                <i class="fas fa-plus mr-1"></i>
                                                Добавить лимит
                                            </b-button>
                                            <small v-if="fuelCardLimitsRequired && !hasValidFuelLimits(material)" class="text-danger d-block mt-1">
                                                Укажите хотя бы один вид топлива и суточный лимит.
                                            </small>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <RequestPpePlanner v-if="itemCatalogType === 'materials'" ref="ppePlanner" :key="form.request_type_id"
                                        :items="form.selectedMaterials" :roster="form.ppeRecipients" :request-type-id="form.request_type_id"
                                        @update:items="form.selectedMaterials = $event" @update:roster="form.ppeRecipients = $event" @active="ppePlannerActive = $event"
                                        @managed-items="ppeManagedItems = $event" @remove="removeMaterial">
                                        <template #add-items>
                                            <div>
                                                <b-button size="sm" variant="primary" class="mr-2" @click="openMaterialSelector">+ Добавить вещь</b-button>
                                                <b-button v-if="canAddCustomCatalogItem" size="sm" variant="link" @click="openCustomMaterialModal">Нет в списке? Ввести название</b-button>
                                            </div>
                                        </template>
                                    </RequestPpePlanner>
                                    <div v-if="!ppePlannerActive" class="d-flex justify-content-between align-items-center mb-2">
                                        <span>{{ itemCatalogLabel }}:</span>
                                        <div>
                                            <b-button variant="outline-primary" size="sm" @click="openMaterialSelector" class="mr-2">
                                                <i class="fas fa-list"></i> Из справочника
                                            </b-button>
                                            <b-button
                                                v-if="canAddCustomCatalogItem"
                                                variant="outline-success"
                                                size="sm"
                                                @click="openCustomMaterialModal"
                                                class="mt-2 mt-sm-0"
                                            >
                                                <i class="fas fa-plus"></i> Добавить вручную
                                            </b-button>
                                        </div>
                                    </div>

                                    <template v-if="isFuelCardCatalog">
                                        <div v-if="!form.selectedMaterials.length" class="text-muted">
                                            {{ itemCatalogLabel }} не выбраны
                                        </div>
                                        <div
                                            v-for="(material, index) in form.selectedMaterials"
                                            :key="material.id || material.catalog_item_id || `fuel-card-selected-${index}`"
                                            class="fuel-card-selected-item border rounded p-3 mb-3"
                                        >
                                            <div class="d-flex justify-content-between align-items-start mb-3">
                                                <div class="pr-3">
                                                    <div class="font-weight-bold">
                                                        {{ material.name || material.item_name || 'Топливная карта' }}
                                                    </div>
                                                    <small v-if="formatFuelLimits(material.current_fuel_limits)" class="text-muted d-block mt-1">
                                                        Текущие лимиты в сутки: {{ formatFuelLimits(material.current_fuel_limits) }}
                                                    </small>
                                                </div>
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    title="Удалить карту"
                                                    @click="removeMaterial(index)"
                                                >
                                                    <i class="fas fa-times"></i>
                                                </b-button>
                                            </div>

                                            <div class="fuel-card-selected-grid">
                                                <b-form-group
                                                    v-if="fuelCardNeedsRecipient && !isFuelCardIssueToRecipient"
                                                    label="Получатель карты *"
                                                    class="mb-0"
                                                >
                                                    <v-select
                                                        v-model="material.target_user_id"
                                                        :options="fuelCardRecipientOptions"
                                                        label="text"
                                                        :reduce="option => option.value"
                                                        :clearable="false"
                                                        placeholder="Выберите сотрудника"
                                                    />
                                                    <EmployeeSelectionPreview :user-id="material.target_user_id" :options="fuelCardRecipientOptions" />
                                                </b-form-group>

                                                <div v-if="fuelCardCanEditLimits" class="fuel-card-limits-panel">
                                                    <div class="small text-muted mb-2">Лимиты в сутки</div>
                                                    <div
                                                        v-for="(limit, limitIndex) in material.fuel_limits"
                                                        :key="`fuel-limit-row-${material.id || material.catalog_item_id || index}-${limitIndex}`"
                                                        class="fuel-card-limit-row mb-2"
                                                    >
                                                        <b-form-select
                                                            v-model.number="limit.fuel_type_id"
                                                            :options="fuelLimitTypeOptions"
                                                            size="sm"
                                                            data-fuel-limit-type
                                                            :data-fuel-limit-material-index="index"
                                                            :data-fuel-limit-index="limitIndex"
                                                            @change="setFuelLimitType(limit, $event)"
                                                        />
                                                        <b-form-input
                                                            v-model.number="limit.value"
                                                            type="number"
                                                            min="0"
                                                            step="0.001"
                                                            size="sm"
                                                            placeholder="Лимит в сутки"
                                                            data-fuel-limit-value
                                                            :data-fuel-limit-material-index="index"
                                                            :data-fuel-limit-index="limitIndex"
                                                            @input="setFuelLimitValue(limit, $event)"
                                                            @change="setFuelLimitValue(limit, $event)"
                                                        />
                                                        <b-button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            title="Удалить лимит"
                                                            @click="removeFuelLimitRow(material, limitIndex)"
                                                        >
                                                            <i class="fas fa-times"></i>
                                                        </b-button>
                                                    </div>
                                                    <b-button size="sm" variant="outline-primary" @click="addFuelLimitRow(material)">
                                                        <i class="fas fa-plus mr-1"></i>
                                                        Добавить лимит
                                                    </b-button>
                                                    <small v-if="fuelCardLimitsRequired && !hasValidFuelLimits(material)" class="text-danger d-block mt-1">
                                                        Укажите хотя бы один вид топлива и суточный лимит.
                                                    </small>
                                                    <small v-else-if="!fuelCardLimitsRequired && !hasValidFuelLimits(material)" class="text-muted d-block mt-1">
                                                        Если лимит не указать, действующие лимиты карты не изменятся.
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-else-if="isMechanicAssetCatalog">
                                        <div v-if="!form.selectedMaterials.length" class="text-muted">
                                            {{ itemCatalogLabel }} не выбраны
                                        </div>
                                        <b-form-group
                                            v-if="mechanicAssetNeedsTargetUser"
                                            label="МОЛ / получатель на все позиции"
                                            class="mb-3"
                                        >
                                            <div class="mechanic-default-target-user-control">
                                                <v-select
                                                    :value="mechanicDefaultTargetUserId"
                                                    :options="mechanicAssetUserOptions"
                                                    label="text"
                                                    :reduce="option => option.value"
                                                    :clearable="true"
                                                    placeholder="Выберите общего МОЛ"
                                                    @input="applyMechanicDefaultTargetUser"
                                                />
                                                <EmployeeSelectionPreview :user-id="mechanicDefaultTargetUserId" :options="mechanicAssetUserOptions" />
                                                <b-button
                                                    variant="outline-primary"
                                                    class="mechanic-default-target-user-self"
                                                    :disabled="!mechanicCurrentUserId"
                                                    @click="applyMechanicDefaultTargetUserToSelf"
                                                >
                                                    <i class="fas fa-user-check mr-1"></i>
                                                    Себе
                                                </b-button>
                                            </div>
                                        </b-form-group>
                                        <div
                                            v-for="(material, index) in form.selectedMaterials"
                                            :key="`mechanic-selected-${material.catalog_item_id || material.id || 'custom'}-${index}`"
                                            :class="[
                                                'mechanic-asset-selected-item',
                                                {
                                                    'mechanic-asset-selected-item--quantity': isMechanicQuantityMaterial(material),
                                                    'mechanic-asset-selected-item--missing-mol': mechanicAssetNeedsTargetUser && !material.target_user_id && !mechanicDefaultTargetUserId
                                                }
                                            ]"
                                        >
                                            <div class="mechanic-asset-selected-header">
                                                <div class="mechanic-asset-selected-index">{{ index + 1 }}</div>
                                                <div class="mechanic-asset-selected-main">
                                                    <b-form-input
                                                        v-if="material.isCustom === true || material.is_custom === true"
                                                        :value="material.name || material.item_name || ''"
                                                        placeholder="Название техники"
                                                        aria-label="Название техники, добавленной вручную"
                                                        size="sm"
                                                        :state="String(material.name || material.item_name || '').trim() ? null : false"
                                                        @input="onMaterialUpdate({ index, field: 'name', value: $event })"
                                                    />
                                                    <div v-else class="mechanic-asset-selected-title">
                                                        {{ material.name || material.item_name || 'Техника' }}
                                                    </div>
                                                    <div class="mechanic-asset-selected-meta">
                                                        <span v-if="material.catalog_name">
                                                            <i class="fas fa-layer-group"></i>
                                                            {{ material.catalog_name }}
                                                        </span>
                                                        <span v-if="material.inventory_number">
                                                            <i class="fas fa-hashtag"></i>
                                                            {{ material.inventory_number }}
                                                        </span>
                                                        <span v-if="material.current_meter_value !== null && material.current_meter_value !== undefined">
                                                            <i class="fas fa-tachometer-alt"></i>
                                                            {{ material.current_meter_value }} {{ getMechanicMeterTypeText(material.current_meter_type) }}
                                                        </span>
                                                        <span v-if="isMechanicQuantityMaterial(material)">
                                                            <i class="fas fa-boxes"></i>
                                                            Количественная
                                                        </span>
                                                        <span
                                                            v-if="isMechanicMaterialUsedMoreThanOnce(material)"
                                                            class="mechanic-asset-used-marker"
                                                        >
                                                            <i class="fas fa-link"></i>
                                                            Уже используется в заявке
                                                        </span>
                                                    </div>
                                                </div>
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    class="mechanic-asset-selected-remove"
                                                    title="Удалить технику"
                                                    @click="removeMaterial(index)"
                                                >
                                                    <i class="fas fa-times"></i>
                                                </b-button>
                                            </div>

                                            <div class="mechanic-asset-selected-grid">
                                                <b-form-group
                                                    v-if="isMechanicQuantityMaterial(material)"
                                                    label="Количество *"
                                                >
                                                    <b-input-group>
                                                        <b-form-input
                                                            v-model.number="material.quantity"
                                                            type="number"
                                                            min="0.001"
                                                            step="0.001"
                                                        />
                                                        <b-input-group-append>
                                                            <b-input-group-text>
                                                                {{ getMechanicQuantityUnit(material) }}
                                                            </b-input-group-text>
                                                        </b-input-group-append>
                                                    </b-input-group>
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetNeedsTargetUser"
                                                    label="МОЛ / получатель"
                                                >
                                                    <v-select
                                                        :value="material.target_user_id || null"
                                                        :options="mechanicAssetUserOptions"
                                                        label="text"
                                                        :reduce="option => option.value"
                                                        :clearable="true"
                                                        :placeholder="mechanicDefaultTargetUserId ? 'Как общий МОЛ' : 'Выберите МОЛ'"
                                                        @input="setMechanicMaterialTargetUser(material, $event)"
                                                    />
                                                    <EmployeeSelectionPreview :user-id="material.target_user_id || null" :options="mechanicAssetUserOptions" />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetNeedsWarehouse"
                                                    label="Склад / ремзона *"
                                                >
                                                    <b-form-select
                                                        v-model.number="material.target_warehouse_id"
                                                        :options="mechanicAssetWarehouseOptions"
                                                    />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetNeedsRental"
                                                    label="Арендатор *"
                                                >
                                                    <b-form-input
                                                        v-model.trim="material.rental_counterparty"
                                                        placeholder="Название арендатора"
                                                    />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetNeedsRental"
                                                    label="Оператор-МОЛ *"
                                                >
                                                    <v-select
                                                        v-model="material.rental_operator_user_id"
                                                        :options="mechanicAssetUserOptions"
                                                        label="text"
                                                        :reduce="option => option.value"
                                                        :clearable="false"
                                                        placeholder="Выберите сотрудника"
                                                    />
                                                    <EmployeeSelectionPreview :user-id="material.rental_operator_user_id" :options="mechanicAssetUserOptions" />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetNeedsRental"
                                                    label="Ожидаемый возврат"
                                                >
                                                    <b-form-input
                                                        v-model="material.rental_expected_return_at"
                                                        type="date"
                                                    />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetRequiresMeterValue"
                                                    label="Тип счетчика *"
                                                >
                                                    <b-form-select
                                                        v-model="material.meter_type"
                                                        :options="mechanicAssetMeterTypeOptions"
                                                    />
                                                </b-form-group>

                                                <b-form-group
                                                    v-if="mechanicAssetRequiresMeterValue"
                                                    label="Показание *"
                                                >
                                                    <b-form-input
                                                        v-model.number="material.meter_value"
                                                        type="number"
                                                        min="0"
                                                        step="0.001"
                                                    />
                                                </b-form-group>
                                            </div>

                                            <b-form-group
                                                label="Примечание по позиции"
                                                class="mb-0 mt-2"
                                            >
                                                <AppTextarea
                                                    v-model.trim="material.notes"
                                                    placeholder="Пожелания, комплектация, условия работы"
                                                />
                                            </b-form-group>
                                        </div>
                                    </template>

                                    <MaterialsList
                                        v-else
                                        :materials="form.selectedMaterials"
                                        :employee-catalog="isEmployeeCatalog"
                                        :editable="true"
                                        :lock-directory-names="true"
                                        :hidden-materials="ppePlannerActive ? ppeManagedItems : []"
                                        :show-remove-button="true"
                                        :show-quantity="showCatalogItemQuantity"
                                        :empty-text="`${itemCatalogLabel} не выбраны`"
                                        @update:material="onMaterialUpdate"
                                        @remove:material="removeMaterial"
                                    >
                                        <template #item-extra="{ material }">
                                            <RequestItemPpe :value="material.ppe_data" :item="material" :editable="!ppePlannerActive" :request-type-id="form.request_type_id" @input="$set(material, 'ppe_data', $event)" />
                                        </template>
                                    </MaterialsList>
                                </template>
                            </div>
                            <b-form-invalid-feedback :state="!hasItemCatalog || isItemCatalogSelectionValid">
                                {{ itemCatalogValidationText }}
                            </b-form-invalid-feedback>
                        </b-form-group>

                        <!-- Описание для заявок со справочником позиций -->
                        <b-form-group
                            v-if="hasItemCatalog && showDescriptionField"
                            :label="descriptionFieldLabel"
                            label-for="description-materials"
                        >
                            <DiscussionEditor
                                id="description-materials"
                                field="description" :context="{type: 'request', id: 'new'}"
                                :mention-state="form.description_mention_state" @mention-state="form.description_mention_state = $event"
                                v-model="form.description"
                                placeholder="Опишите суть заявки (Ctrl+V для вставки файлов из буфера)"
                                :required="isDescriptionRequired"
                                :state="descriptionFieldState"
                                @paste="onPasteToDescription"
                            />
                            <b-form-invalid-feedback :state="descriptionFieldState">
                                Введите описание заявки
                            </b-form-invalid-feedback>
                        </b-form-group>

                    </template>
                </div>
                </div>

                <!-- Файлы к описанию -->
                <b-form-group
                    v-if="showAttachmentField"
                    :label="attachmentFieldLabel"
                    label-for="description-files"
                >
                    <div class="d-flex align-items-center gap-2">
                        <b-form-file
                            id="description-files"
                            v-model="descriptionFilesInput"
                            :state="attachmentFieldState"
                            multiple
                            placeholder="Выберите файлы или перетащите их сюда"
                            drop-placeholder="Перетащите файлы сюда..."
                            browse-text="Обзор"
                            @input="onDescriptionFilesChange"
                            class="flex-grow-1"
                        />
                        <b-button
                            variant="outline-secondary"
                            @click="pasteFromClipboardToDescription"
                            v-b-tooltip.hover
                            title="Вставить файл из буфера обмена"
                            class="clipboard-paste-button"
                        >
                            <i class="fas fa-paste mr-1"></i>
                            <span class="d-none d-md-inline">Вставить из буфера обмена</span>
                        </b-button>
                    </div>
                    <b-form-invalid-feedback :state="attachmentFieldState">
                        Прикрепите хотя бы один файл.
                    </b-form-invalid-feedback>
                    <div v-if="filesWithPreview && filesWithPreview.length" class="mt-2 files-parent">
                        <div
                            v-for="(file, index) in filesWithPreview"
                            :key="getFileKey(file, index)"
                            class="file-container mx-1"
                        >
                            <i
                                class="fas fa-times text-danger small remove-icon"
                                title="Удалить"
                                @click="removeDescriptionFile(index)"
                            />
                            <div
                                class="d-flex justify-content-center align-items-center file-preview"
                                @click="openFilePreview(file)"
                            >
                                <img v-if="file.isImage" :src="file.previewUrl" :alt="file.name">
                                <i v-else :class="`${file.iconClass} fa-2x text-secondary`"></i>
                            </div>
                            <div class="link-text">
                                <div class="file-name" :title="file.name">
                                    {{ file.name }}
                                </div>
                                <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                            </div>
                        </div>
                    </div>
                </b-form-group>

                <div
                    v-if="canFillRequestDetails && requiredAttachmentChoiceRulesForCreate.length"
                    class="border rounded p-3 mb-3 bg-light"
                >
                    <h6 class="mb-3">
                        <i class="fas fa-tasks"></i>
                        Требования к исполнителю заявки
                    </h6>
                    <b-form-checkbox
                        v-for="rule in requiredAttachmentChoiceRulesForCreate"
                        :key="rule.key"
                        v-model="form.requiredAttachmentChoices[rule.key]"
                        @change="value => setRequiredAttachmentChoice(rule.key, value)"
                        switch
                        class="mb-2"
                    >
                        Требовать: {{ rule.label }}
                    </b-form-checkbox>
                </div>

                <!-- Обязательные документы по настройкам типа -->
                <div
                    v-if="canFillRequestDetails && requiredAttachmentRulesForCreate.length"
                    class="border rounded p-3 mb-3 bg-light"
                >
                    <h6 class="mb-3">
                        <i class="fas fa-paperclip"></i>
                        Обязательные документы
                    </h6>
                    <b-form-group
                        v-for="rule in requiredAttachmentRulesForCreate"
                        :key="rule.key"
                        :label="getRequiredAttachmentLabel(rule)"
                    >
                        <div class="d-flex align-items-center gap-2">
                            <b-form-file
                                :value="requiredAttachmentInputs[rule.key] || null"
                                :state="getRequiredAttachmentState(rule)"
                                multiple
                                placeholder="Выберите файлы или перетащите их сюда"
                                drop-placeholder="Перетащите файлы сюда..."
                                browse-text="Обзор"
                                class="flex-grow-1"
                                @input="onRequiredAttachmentFilesChange(rule, $event)"
                            />
                            <b-button
                                type="button"
                                variant="outline-secondary"
                                @click="pasteFromClipboardToRequiredAttachment(rule)"
                                v-b-tooltip.hover
                                title="Вставить файл из буфера обмена"
                                class="clipboard-paste-button"
                            >
                                <i class="fas fa-paste mr-1"></i>
                                <span class="d-none d-md-inline">Вставить из буфера обмена</span>
                            </b-button>
                        </div>
                        <b-form-invalid-feedback :state="getRequiredAttachmentState(rule)">
                            {{ getRequiredAttachmentMissingText(rule) }}
                        </b-form-invalid-feedback>
                        <div
                            v-if="getRequiredAttachmentFiles(rule).length"
                            class="mt-2"
                        >
                            <div
                                v-for="(file, index) in getRequiredAttachmentFiles(rule)"
                                :key="getFileKey(file, index)"
                                class="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-2 bg-white"
                            >
                                <div
                                    class="text-truncate mr-2 request-create-required-file-preview"
                                    @click="openRequiredAttachmentPreview(rule, file, index)"
                                >
                                    <i :class="`${getLocalFileIconClass(file)} mr-2`"></i>
                                    {{ file.name }}
                                    <small class="text-muted ml-2">{{ formatFileSize(file.size) }}</small>
                                </div>
                                <b-button
                                    size="sm"
                                    variant="outline-danger"
                                    @click.stop="removeRequiredAttachmentFile(rule, index)"
                                >
                                    <i class="fas fa-times"></i>
                                </b-button>
                            </div>
                        </div>
                    </b-form-group>
                </div>

                <b-alert v-if="submissionError || (!form.recurrence && form.deadline && deadlinePolicyResult.code)" show variant="danger" role="alert">
                    {{ submissionError || deadlinePolicyResult.message }}
                </b-alert>
                <!-- Кнопки действий (показываем только после выбора типа заявки) -->
                <div v-if="form.request_type_id" class="d-flex justify-content-between align-items-center flex-wrap">
                    <b-button variant="outline-secondary" size="sm" @click="$router.go(-1)">
                        <i class="fas fa-times"></i> 
                        <span class="d-none d-sm-inline">Отмена</span>
                    </b-button>
                    <div class="mt-2 mt-sm-0">
                        <b-button 
                            variant="outline-primary" 
                            type="button" 
                            size="sm"
                            @click="saveDraft" 
                            class="mr-2"
                            :disabled="!isFormValidForDraft || savingDraft || submittingRequest"
                        >
                            <template v-if="savingDraft">
                                <b-spinner small class="mr-1"></b-spinner>
                                <span class="d-none d-sm-inline">Сохранение...</span>
                                <span class="d-sm-none">Сохранение...</span>
                            </template>
                            <template v-else>
                                <i class="fas fa-save"></i> 
                                <span class="d-none d-sm-inline">Сохранить как черновик</span>
                                <span class="d-sm-none">Черновик</span>
                            </template>
                        </b-button>
                        <b-button 
                            variant="primary" 
                            type="button"
                            size="sm"
                            @click="submitRequest"
                            :disabled="!isFormValid || savingDraft || submittingRequest"
                        >
                            <template v-if="submittingRequest">
                                <b-spinner small class="mr-1"></b-spinner>
                                <span class="d-none d-sm-inline">Создание...</span>
                                <span class="d-sm-none">Создание...</span>
                            </template>
                            <template v-else>
                                <i class="fas fa-paper-plane"></i> 
                                <span class="d-none d-sm-inline">Создать заявку</span>
                                <span class="d-sm-none">Создать</span>
                            </template>
                        </b-button>
                    </div>
                </div>
            </b-form>
        </b-card>

        <!-- Модальное окно добавления материала вручную -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showAddCustomMaterial"
            :title="`Добавить вручную: ${itemCatalogLabel}`"
            @ok="addCustomMaterial"
            @hidden="resetCustomMaterialForm"
            ok-title="Добавить"
            cancel-title="Отмена"
            scrollable
        >
            <b-form @submit.prevent="addCustomMaterial">
                <b-form-group :label="`${customItemNameLabel} *`" label-for="custom-material-name">
                    <b-form-input
                        id="custom-material-name"
                        v-model="customMaterial.name"
                        :placeholder="customItemNamePlaceholder"
                        required
                    />
                </b-form-group>

                <b-row v-if="showCustomQuantityFields">
                    <b-col md="6">
                        <b-form-group label="Количество *" label-for="custom-material-quantity">
                            <b-form-input
                                id="custom-material-quantity"
                                v-model.number="customMaterial.quantity"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="100"
                                required
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="Единица измерения *" label-for="custom-material-unit">
                            <b-form-input
                                id="custom-material-unit"
                                v-model="customMaterial.unit_of_measurement"
                                placeholder="кг, м, шт и т.д."
                                required
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group label="Примечания (необязательно)" label-for="custom-material-notes">
                    <AppTextarea
                        id="custom-material-notes"
                        v-model="customMaterial.notes"
                        placeholder="Примечания к материалу"
                        maxlength="1000"
                    />
                </b-form-group>

                <b-alert variant="info" show>
                    <i class="fas fa-info-circle"></i>
                    <strong>Внимание:</strong> Позиция добавляется только в эту заявку.
                    <span v-if="!isEmployeeCatalog"> Для добавления в общий справочник обратитесь к администратору.</span>
                </b-alert>
            </b-form>
        </b-modal>

        <!-- Модальное окно выбора позиций -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-if="!isMechanicAssetCatalog"
            id="material-selector-modal"
            v-model="showMaterialSelector"
            :title="`Выбор: ${itemCatalogLabel}`"
            size="lg"
            scrollable
            :ok-title="`Добавить выбранные (${availableMaterials.filter(m => m.selected).length})`"
            :cancel-title="`Отмена`"
            @ok="addSelectedMaterials"
            @cancel="clearMaterialSelection"
        >
            <div v-if="materialsLoading" class="text-center text-muted">
                <i class="fas fa-spinner fa-spin"></i> Загрузка справочника...
            </div>
            <div v-else-if="availableMaterials.length">
                <b-form-input
                    v-model="materialSearch"
                    :placeholder="`Поиск: ${itemCatalogLabel.toLowerCase()}...`"
                    class="mb-3"
                />
                
                <div class="border rounded" style="max-height: 400px; overflow-y: auto;">
                    <div v-for="material in filteredMaterials" :key="material.id" 
                         class="d-flex justify-content-between align-items-center p-2 border-bottom">
                        <div>
                            <div>
                                <strong>
                                    {{ material.name }}
                                    <span v-if="!isEmployeeCatalog" class="text-info">{{ material.unit_of_measurement }}</span>
                                </strong>
                                <small v-if="isMechanicAssetCatalog && material.catalog_name" class="d-block text-muted">
                                    Группа: {{ material.catalog_name }}
                                </small>
                                <small v-if="isMechanicAssetCatalog && material.inventory_number" class="d-block text-muted">
                                    Инв.: {{ material.inventory_number }}
                                </small>
                                <small v-if="isMechanicAssetCatalog && material.accounting_type === 'quantity'" class="d-block text-muted">
                                    Доступно: {{ material.available_quantity || 0 }} {{ material.unit_of_measurement }}
                                </small>
                                <small v-if="fuelCardCanEditLimits && formatFuelLimits(material.fuel_limits)" class="d-block text-muted">
                                    Текущие лимиты в сутки: {{ formatFuelLimits(material.fuel_limits) }}
                                </small>
                            </div>
                        </div>
                        <b-form-checkbox
                            v-model="material.selected"
                            :value="true"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="text-center text-muted">
                <i class="fas fa-exclamation-triangle"></i> Позиции не найдены
            </div>
        </b-modal>

        <mechanic-asset-picker-modal
            v-if="isMechanicAssetCatalog"
            v-model="showMechanicMaterialSelector"
            :title="`Выбор: ${itemCatalogLabel}`"
            :request-type-id="form.request_type_id"
            :building-process-id="isMechanicAssetCatalogOnObject ? form.building_process_id : null"
            :used-ids="mechanicMaterialSelectorUsedIds"
            multiple
            :clearable="false"
            @confirm="addMechanicCatalogPickerMaterials"
        />

        <!-- Модальное окно восстановления черновика -->
        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="showRestoreDraftModal"
            title="Восстановить черновик?"
            @ok="restoreDraft"
            @cancel="startFresh"
            ok-title="Восстановить"
            cancel-title="Начать заново"
            ok-variant="primary"
            cancel-variant="secondary"
            centered
            no-close-on-backdrop
        >
            <div v-if="$store.state.requests.availableDraft">
                <p>
                    Найден сохраненный черновик для этого типа заявки.
                </p>
                <p v-if="getLastSavedDate()" class="text-muted small">
                    <i class="fas fa-clock"></i> Последнее сохранение: {{ getLastSavedDate() }}
                </p>
                <p class="mb-0">
                    Хотите восстановить данные из черновика или начать заполнение заново?
                </p>
                <b-alert variant="warning" show class="mt-3">
                    <i class="fas fa-exclamation-triangle"></i>
                    <strong>Внимание:</strong> При восстановлении файлы не будут загружены. 
                    Пожалуйста, загрузите их заново при необходимости.
                </b-alert>
            </div>
        </b-modal>
    </div>
</template>

<script>
import RequestDeadlineInput from '../components/RequestDeadlineInput.vue';
import {deadlinePolicy, deadlineParts} from '@/utils/requestDeadline';
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
import ReportAnnulmentEditor from '../components/ReportAnnulmentEditor.vue';
import RequestRecurrenceSchedule from '../components/RequestRecurrenceSchedule.vue';
const recurrenceDomain = require('@/utils/requestRecurrence');
import RequestItemPpe from '@/components/RequestItemPpe.vue';
import RequestPpePlanner from '@/components/RequestPpePlanner.vue';
import {copyPpeSnapshot} from '@/utils/requestPpe';
import clipboardFileMixin from '@/mixins/clipboardFileMixin.js';
import MaterialsList from '@/components/MaterialsList/MaterialsList.vue';
import MechanicAssetPickerModal from '../RequestCard/MaterialsTable/MechanicAssetPickerModal.vue';
import CustomFieldsForm from '../components/CustomFieldsForm.vue';
import { REQUEST_CATEGORIES, normalizeRequestCategory } from '@/constants/requestCategories';
import { normalizeRequestTypeIcon } from '@/constants/requestTypeIcons';
import {
    buildFuelTypeById,
    buildFuelTypeOptions,
    cloneFuelLimits as cloneFuelLimitsValue,
    formatFuelLimits as formatFuelLimitsValue,
    sanitizeFuelLimits as sanitizeFuelLimitsValue,
    setFuelLimitType as setFuelLimitTypeValue,
    setFuelLimitValue as setFuelLimitAmountValue,
    syncFuelLimitControls
} from '@/utils/fuelLimits';
import {
    normalizeRequiredAttachmentRule as normalizeRequiredAttachmentRuleConfig,
    isRequiredAttachmentRuleEnabledByChoices
} from '@/utils/requestRequiredAttachments';
import {
    fileIconClass,
    formatFileSize as formatViewerFileSize,
    isImageFile as isViewerImageFile
} from '@/utils/fileViewer';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

const RequestDetailsPrerequisites = {
    name: 'RequestDetailsPrerequisites',
    props: {
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        items: {
            type: Array,
            required: true
        }
    },
    render(h) {
        return h('div', { class: 'request-details-lock border rounded bg-light mb-3' }, [
            h('div', { class: 'font-weight-bold mb-1' }, this.title),
            h('div', { class: 'text-muted small mb-3' }, this.description),
            h('div', { class: 'request-prerequisite-grid' }, this.items.map(item =>
                h('div', {
                    key: item.key,
                    class: [
                        'request-prerequisite-card',
                        { 'request-prerequisite-card--done': item.done }
                    ]
                }, [
                    h('div', { class: 'request-prerequisite-card__layout' }, [
                        h('i', {
                            class: [
                                item.icon,
                                'request-choice-icon',
                                'mr-3',
                                item.done ? 'text-success' : 'text-secondary'
                            ]
                        }),
                        h('div', { class: 'request-choice-card__text' }, [
                            h('div', { class: 'font-weight-bold request-choice-title' }, item.title),
                            h('div', { class: 'small text-muted mt-1 request-choice-description' }, item.description)
                        ])
                    ])
                ])
            ))
        ]);
    }
};

export default {
    name: "CreateRequest",
    components: {
        RequestDeadlineInput,
        EmployeeSelectionPreview,
        ReportAnnulmentEditor,
        RequestRecurrenceSchedule,
        RequestItemPpe,
        RequestPpePlanner,
        MechanicAssetPickerModal,
        MaterialsList,
        CustomFieldsForm,
        RequestDetailsPrerequisites
    },
    mixins: [clipboardFileMixin],
    data() {
        return {
            submissionError: '',
            form: {
                report_annulment: {reports: []},
                request_type_id: null,
                title: '',
                description: '',
                description_mention_state: null,
                priority: 'normal',
                deadline: null,
                urgency_comment: '',
                building_process_id: null,
                selectedMaterials: [],
                ppeRecipients: [],
                attachments: [],
                requiredAttachments: {},
                requiredAttachmentChoices: {},
                recurrence: null,
                custom_fields: {}
            },
            showMaterialSelector: false,
            ppePlannerActive: false,
            ppeManagedItems: [],
            showMechanicMaterialSelector: false,
            showAddCustomMaterial: false,
            materialSearch: '',
            customMaterial: {
                name: '',
                quantity: 0,
                unit_of_measurement: '',
                notes: ''
            },
            priorityOptions: [
                { value: 'low', text: 'Низкий' },
                { value: 'normal', text: 'Обычный' },
                { value: 'high', text: 'Высокий' },
                { value: 'urgent', text: 'Срочный' }
            ],
            buildingProcesses: [],
            buildingProcessesLoading: false,
            availableMaterials: [],
            materialsLoading: false,
            loadedItemCatalogKey: null,
            fuelCardRecipientUserId: null,
            mechanicDefaultTargetUserId: null,
            lastMechanicDefaultTargetUserId: null,
            suppressMechanicDefaultTargetUserApply: false,
            mechanicAssetDictionariesLoaded: false,
            mechanicAssetMeterTypeOptions: [
                { value: null, text: 'Выберите счетчик' },
                { value: 'km', text: 'Километры' },
                { value: 'moto_hour', text: 'Моточасы' }
            ],
            descriptionFilesInput: null,
            requiredAttachmentInputs: {},
            filePreviewUrls: new Map(), // Храним URL для каждого файла
            savingDraft: false,
            submittingRequest: false,
            selectedRequestCategory: null,
            requestCategoryStepCollapsed: false,
            extraRequestTypes: [],
            requestTypesLoaded: false,
            requestTypePickerExpanded: true,
            requestTypeSearch: '',
            // Автосохранение черновиков
            autoSaveTimer: null,
            showRestoreDraftModal: false,
            draftRestoreShown: false, // Флаг чтобы показать модальное окно только один раз
            lastAutoSaveTime: null,
            // Загрузка данных для копирования
            loadingSourceRequest: false,
            sourceRequestLoaded: false,
            recurrenceInheritedFiles: [],
            linkedRequestContextLoaded: false,
            linkedSourceRequestId: null,
            linkedRuleKey: null,
            linkedSourceRequest: null,
            linkedSourceRule: null,
            telegramBackButtonHandler: null,
            telegramHistoryBackInProgress: false
        }
    },
    computed: {
        isReportAnnulment() { return this.selectedRequestType && this.selectedRequestType.category === 'report_annulment'; },
        recurrenceAvailable() { return Boolean(this.selectedRequestType && recurrenceDomain.config(this.selectedRequestType).enabled && recurrenceDomain.supports(this.selectedRequestType)); },
        requestTypes() {
            const baseTypes = this.$store.state.requests.requestTypes || [];
            const baseIds = new Set(baseTypes.map(type => Number(type.id)));
            const extraTypes = (this.extraRequestTypes || [])
                .filter(type => type && !baseIds.has(Number(type.id)));
            const types = baseTypes.concat(extraTypes);
            return types.map(rt => ({
                value: rt.id,
                text: rt.name,
                description: rt.description,
                category: rt.category,
                normalizedCategory: normalizeRequestCategory(rt.category),
                is_active: rt.is_active,
                submission_deadline_days: rt.submission_deadline_days,
                is_emergency: rt.is_emergency,
                requires_justification_if_late: rt.requires_justification_if_late,
                requires_title: rt.requires_title,
                allow_attachments: rt.allow_attachments,
                attachments_required: rt.attachments_required,
                custom_fields: Array.isArray(rt.custom_fields) ? rt.custom_fields : [],
                workflow_config: rt.workflow_config,
                icon: normalizeRequestTypeIcon(rt.workflow_config && rt.workflow_config.requestTypeIcon),
                late_approval_config: rt.late_approval_config
            }));
        },
        activeRequestTypes() {
            return this.requestTypes.filter(type => type.is_active !== false && type.is_active !== 0);
        },
        requestTypeSearchQuery() {
            return this.normalizeRequestTypeSearch(this.requestTypeSearch);
        },
        filteredRequestTypes() {
            const query = this.requestTypeSearchQuery;
            if (!query) {
                return this.activeRequestTypes;
            }
            return this.activeRequestTypes.filter(type => this.requestTypeMatchesSearch(type, query));
        },
        visibleRequestCategories() {
            const query = this.requestTypeSearchQuery;
            const typesByCategory = this.filteredRequestTypes.reduce((acc, type) => {
                acc[type.normalizedCategory] = true;
                return acc;
            }, {});
            const activeTypesByCategory = this.activeRequestTypes.reduce((acc, type) => {
                acc[type.normalizedCategory] = true;
                return acc;
            }, {});
            return REQUEST_CATEGORIES.filter(category => {
                const hasTypes = typesByCategory[category.value];
                if (!query) {
                    return hasTypes;
                }
                return hasTypes || (activeTypesByCategory[category.value] && this.requestCategoryMatchesSearch(category, query));
            });
        },
        selectedCategoryRequestTypes() {
            if (!this.selectedRequestCategory) {
                return [];
            }

            return this.filteredRequestTypes.filter(type => type.normalizedCategory === this.selectedRequestCategory);
        },
        requestTypePickerResults() {
            return this.requestTypeSearchQuery
                ? this.filteredRequestTypes
                : this.selectedCategoryRequestTypes;
        },
        selectedRequestCategoryOption() {
            if (!this.selectedRequestCategory) {
                return null;
            }

            return REQUEST_CATEGORIES.find(category => category.value === this.selectedRequestCategory) || null;
        },
        minDate() {
            return new Date().toISOString().split('T')[0];
        },
        isMaterialRequest() {
            return this.hasItemCatalog;
        },
        selectedRequestType() {
            return this.requestTypes.find(type => type.value === this.form.request_type_id);
        },
        customFieldDefinitions() {
            const fields = this.selectedRequestType && Array.isArray(this.selectedRequestType.custom_fields)
                ? this.selectedRequestType.custom_fields
                : [];
            return fields.filter(field =>
                field &&
                String(field.key || '').trim() &&
                String(field.label || '').trim()
            );
        },
        hasCustomFields() {
            return this.customFieldDefinitions.length > 0;
        },
        visibleCustomFieldDefinitions() {
            return this.getVisibleCustomFieldDefinitions(this.form.custom_fields || {});
        },
        isLinkedRequestFlow() {
            return Boolean(this.linkedSourceRequestId && this.linkedRuleKey);
        },
        linkedSourceLabel() {
            return (this.linkedSourceRule && this.linkedSourceRule.targetLabel) || 'Основание';
        },
        linkedSourceRoute() {
            return this.linkedSourceRequest
                ? `/requests/${this.linkedSourceRequest.id}`
                : '/requests';
        },
        linkedSourceDisplayNumber() {
            if (!this.linkedSourceRequest) {
                return '';
            }

            return `Заявка #${this.linkedSourceRequest.request_number || this.linkedSourceRequest.id}`;
        },
        linkedSourceTitle() {
            return this.linkedSourceRequest && this.linkedSourceRequest.title
                ? this.linkedSourceRequest.title
                : '';
        },
        linkedSourceSummaryText() {
            if (!this.linkedSourceRequest) {
                return '';
            }

            const parts = [];
            if (this.linkedSourceRequest.type_name) {
                parts.push(this.linkedSourceRequest.type_name);
            }
            if (this.linkedSourceRequest.building_object_name) {
                parts.push(this.linkedSourceRequest.building_object_name);
            }
            if (this.linkedSourceRequest.building_contract_name) {
                parts.push(`Договор: ${this.linkedSourceRequest.building_contract_name}`);
            }

            return parts.join(' · ');
        },
        selectedBuildingProcess() {
            if (!this.form.building_process_id) {
                return null;
            }

            return this.buildingProcesses.find(process => process.value === this.form.building_process_id) || null;
        },
        itemCatalogConfig() {
            return this.getItemCatalogConfig(this.selectedRequestType);
        },
        hasItemCatalog() {
            return Boolean(this.itemCatalogConfig && this.itemCatalogConfig.enabled);
        },
        buildingProcessConfig() {
            return this.getBuildingProcessConfig(this.selectedRequestType);
        },
        showsBuildingProcess() {
            return this.buildingProcessConfig.enabled ||
                this.mechanicAssetRequiresHeaderObject ||
                this.isMechanicAssetCatalogOnObject;
        },
        requiresBuildingProcess() {
            return this.buildingProcessConfig.required ||
                this.mechanicAssetRequiresHeaderObject ||
                this.isMechanicAssetCatalogOnObject;
        },
        buildingProcessFieldLabel() {
            if (this.isMechanicAssetCatalogOnObject) {
                return this.requiresBuildingProcess ? 'Объект-источник *' : 'Объект-источник';
            }
            if (this.mechanicAssetRequiresHeaderObject) {
                return this.requiresBuildingProcess ? 'Объект назначения *' : 'Объект назначения';
            }
            return this.requiresBuildingProcess ? 'Строительный процесс *' : 'Строительный процесс';
        },
        buildingProcessFieldPlaceholder() {
            if (this.isMechanicAssetCatalogOnObject) {
                return 'Выберите объект-источник';
            }
            if (this.mechanicAssetRequiresHeaderObject) {
                return 'Выберите объект назначения';
            }
            return 'Выберите процесс';
        },
        buildingProcessInvalidFeedback() {
            if (this.isMechanicAssetCatalogOnObject) {
                return 'Выберите объект-источник';
            }
            if (this.mechanicAssetRequiresHeaderObject) {
                return 'Выберите объект назначения';
            }
            return 'Выберите строительный процесс';
        },
        itemCatalogType() {
            return this.itemCatalogConfig && this.itemCatalogConfig.type;
        },
        itemCatalogScope() {
            return (this.itemCatalogConfig && this.itemCatalogConfig.scope) || 'all';
        },
        isEmployeeCatalog() {
            return this.itemCatalogType === 'employees';
        },
        isFuelCardCatalog() {
            return this.itemCatalogType === 'fuel_cards';
        },
        isMechanicAssetCatalog() {
            return this.itemCatalogType === 'mechanic_assets';
        },
        isMechanicAssetCatalogOnObject() {
            return this.isMechanicAssetCatalog && this.itemCatalogScope === 'mechanic_assets_on_object';
        },
        fuelCardActionConfig() {
            const workflowConfig = this.selectedRequestType && this.selectedRequestType.workflow_config
                ? this.selectedRequestType.workflow_config
                : {};
            const source = workflowConfig.fuelCardAction || workflowConfig.fuel_card_action || {};

            return {
                enabled: source.enabled === true,
                operation: source.operation || 'assign_to_initiator'
            };
        },
        fuelCardOperation() {
            return this.fuelCardActionConfig.operation;
        },
        fuelCardNeedsRecipient() {
            return this.isFuelCardCatalog &&
                this.fuelCardActionConfig.enabled === true &&
                ['issue_to_recipient', 'move_to_recipient'].includes(this.fuelCardOperation);
        },
        isFuelCardIssueToRecipient() {
            return this.fuelCardNeedsRecipient && this.fuelCardOperation === 'issue_to_recipient';
        },
        isFuelCardMoveToRecipient() {
            return this.fuelCardNeedsRecipient && this.fuelCardOperation === 'move_to_recipient';
        },
        isFuelCardChangeLimits() {
            return this.isFuelCardCatalog &&
                this.fuelCardActionConfig.enabled === true &&
                this.fuelCardOperation === 'change_limits';
        },
        fuelCardCanEditLimits() {
            return this.isFuelCardCatalog &&
                this.fuelCardActionConfig.enabled === true &&
                ['issue_to_recipient', 'move_to_recipient', 'change_limits'].includes(this.fuelCardOperation);
        },
        fuelCardLimitsRequired() {
            return this.isFuelCardIssueToRecipient || this.isFuelCardChangeLimits;
        },
        mechanicAssetActionConfig() {
            const workflowConfig = this.selectedRequestType && this.selectedRequestType.workflow_config
                ? this.selectedRequestType.workflow_config
                : {};
            const actions = Array.isArray(workflowConfig.mechanicAssetActions || workflowConfig.mechanic_asset_actions)
                ? (workflowConfig.mechanicAssetActions || workflowConfig.mechanic_asset_actions)
                : [];
            const source = actions.find(action => action && action.enabled === true) ||
                workflowConfig.mechanicAssetAction ||
                workflowConfig.mechanic_asset_action ||
                {};

            return {
                enabled: source.enabled === true,
                operation: source.operation || 'move_to_object',
                requireMeterValue: source.requireMeterValue === true || source.require_meter_value === true,
                requireCondition: source.requireCondition === true || source.require_condition === true
            };
        },
        mechanicAssetOperation() {
            return this.mechanicAssetActionConfig.operation;
        },
        mechanicAssetRequiresHeaderObject() {
            return this.isMechanicAssetCatalog &&
                this.mechanicAssetActionConfig.enabled === true &&
                this.mechanicAssetOperation === 'issue_to_object';
        },
        mechanicAssetObjectHandledOutsidePositionCreate() {
            return this.isMechanicAssetCatalog &&
                this.mechanicAssetActionConfig.enabled === true &&
                ['issue_to_object', 'move_to_object'].includes(this.mechanicAssetOperation);
        },
        mechanicAssetNeedsObject() {
            return false;
        },
        mechanicAssetNeedsTargetUser() {
            return this.isMechanicAssetCatalog &&
                this.mechanicAssetActionConfig.enabled === true &&
                ['issue_to_object', 'move_to_object'].includes(this.mechanicAssetOperation);
        },
        mechanicAssetNeedsWarehouse() {
            return this.isMechanicAssetCatalog &&
                this.mechanicAssetActionConfig.enabled === true &&
                ['move_to_warehouse', 'return_to_warehouse', 'return_from_rent'].includes(this.mechanicAssetOperation);
        },
        mechanicAssetNeedsRental() {
            return this.isMechanicAssetCatalog &&
                this.mechanicAssetActionConfig.enabled === true &&
                this.mechanicAssetOperation === 'rent_out';
        },
        mechanicAssetRequiresMeterValue() {
            return false;
        },
        mechanicAssetRequiresCondition() {
            return false;
        },
        mechanicCurrentUserId() {
            const currentUser = this.$store.getters.currentUser || {};
            return this.toPositiveInteger(
                currentUser.userId ||
                currentUser.user_id ||
                currentUser.id
            );
        },
        mechanicCurrentUserOption() {
            const userId = this.mechanicCurrentUserId;
            if (!userId) {
                return null;
            }

            const user = this.getUserById(userId);
            const currentUser = this.$store.getters.currentUser || {};
            if (!user &&
                Object.prototype.hasOwnProperty.call(currentUser, 'active') &&
                !this.isActiveHumanUser(currentUser)) {
                return null;
            }
            const currentUserName = [
                currentUser.firstName || currentUser.first_name || '',
                currentUser.lastName || currentUser.last_name || ''
            ].join(' ').trim();

            return {
                value: userId,
                text: user
                    ? this.formatUserName(user)
                    : (currentUserName || currentUser.fullName || currentUser.full_name || currentUser.login || `Пользователь #${userId}`)
            };
        },
        mechanicAssetUserOptions() {
            const options = (this.$store.state.users || [])
                .filter(user => this.isActiveHumanUser(user))
                .map(user => ({
                    value: Number(this.getUserId(user)),
                    text: this.formatUserName(user)
                }))
                .filter(option => option.value && option.text);
            const currentUserOption = this.mechanicCurrentUserOption;
            if (currentUserOption && !options.some(option => Number(option.value) === Number(currentUserOption.value))) {
                options.push(currentUserOption);
            }
            return options.sort((a, b) => a.text.localeCompare(b.text, 'ru'));
        },
        mechanicAssetObjectOptions() {
            const placeholder = this.form.building_process_id
                ? 'Объект заявки'
                : 'Выберите объект';
            return [{ value: null, text: placeholder }].concat(this.buildingProcesses.map(process => ({
                value: process.value || process.building_process_id,
                text: process.text || process.object_name || process.name || `Объект #${process.value || process.building_process_id}`
            })));
        },
        mechanicAssetWarehouseOptions() {
            const warehouses = this.$store.state.mechanicAssets && this.$store.state.mechanicAssets.warehouses
                ? this.$store.state.mechanicAssets.warehouses
                : [];
            return [{ value: null, text: 'Выберите склад/ремзону' }].concat(warehouses.map(warehouse => ({
                value: warehouse.id,
                text: warehouse.name
            })));
        },
        fuelCardRecipientOptions() {
            return (this.$store.state.users || [])
                .filter(user => this.isActiveHumanUser(user))
                .map(user => ({
                    value: Number(this.getUserId(user)),
                    text: this.formatUserName(user)
                }))
                .filter(option => option.value && option.text)
                .sort((a, b) => a.text.localeCompare(b.text, 'ru'));
        },
        fuelTypeOptions() {
            return buildFuelTypeOptions(this.$store.state.fuelTypes || []);
        },
        fuelLimitTypeOptions() {
            return [{ value: null, text: 'Выберите топливо' }].concat(this.fuelTypeOptions);
        },
        fuelTypeById() {
            return buildFuelTypeById(this.fuelTypeOptions);
        },
        showCatalogItemQuantity() {
            return !this.isEmployeeCatalog && !this.isFuelCardCatalog && !this.isMechanicAssetCatalog;
        },
        canAddCustomCatalogItem() {
            if (this.isFuelCardCatalog) {
                return false;
            }
            if (this.isMechanicAssetCatalog) {
                return true;
            }
            if (this.isEmployeeCatalog) {
                return this.itemCatalogConfig && this.itemCatalogConfig.allow_custom_items === true;
            }
            return true;
        },
        showCustomQuantityFields() {
            return !this.isEmployeeCatalog && !this.isFuelCardCatalog;
        },
        customItemNameLabel() {
            if (this.isEmployeeCatalog) {
                return 'ФИО сотрудника';
            }
            if (this.isMechanicAssetCatalog) {
                return 'Название техники';
            }
            return 'Название материала';
        },
        customItemNamePlaceholder() {
            if (this.isEmployeeCatalog) {
                return 'Например: Иванов Иван';
            }
            if (this.isMechanicAssetCatalog) {
                return 'Например: экскаватор-погрузчик или виброплита';
            }
            return 'Например: Цемент М500';
        },
        itemCatalogLabel() {
            return (this.itemCatalogConfig && this.itemCatalogConfig.label) || 'Позиции';
        },
        itemCatalogValidationText() {
            if (!this.hasValidCustomItemNames) {
                return 'Укажите название каждой позиции, добавленной вручную';
            }
            if (this.isEmployeeCatalog) {
                return 'Выберите хотя бы одного сотрудника';
            }
            if (this.isFuelCardCatalog) {
                if (this.isFuelCardIssueToRecipient) {
                    return 'Выберите получателя топливной карты и укажите суточный лимит';
                }
                if (this.isFuelCardChangeLimits) {
                    return 'Выберите топливную карту и укажите новые суточные лимиты';
                }
                if (this.fuelCardNeedsRecipient) {
                    return 'Выберите топливную карту и получателя';
                }
                return 'Выберите хотя бы одну топливную карту';
            }
            if (this.isMechanicAssetCatalog) {
                return 'Выберите технику и заполните обязательные поля операции';
            }
            return 'Выберите хотя бы одну позицию и укажите количество';
        },
        itemCatalogBlockTitle() {
            if (this.isEmployeeCatalog) {
                return 'Сотрудники';
            }
            if (this.isFuelCardCatalog) {
                return 'Топливные карты';
            }
            if (this.isMechanicAssetCatalog) {
                return 'Техника';
            }
            return `Детали заявки: ${this.itemCatalogLabel}`;
        },
        descriptionFieldConfig() {
            const workflowConfig = this.selectedRequestType && this.selectedRequestType.workflow_config
                ? this.selectedRequestType.workflow_config
                : {};
            const config = workflowConfig.descriptionField || {};
            return {
                enabled: config.enabled !== false,
                required: Boolean(config.required)
            };
        },
        showDescriptionField() {
            return this.descriptionFieldConfig.enabled;
        },
        isDescriptionRequired() {
            return this.showDescriptionField && this.descriptionFieldConfig.required;
        },
        attachmentConfig() {
            const requestType = this.selectedRequestType || {};
            const enabled = requestType.allow_attachments !== false;
            return {
                enabled,
                required: enabled && Boolean(requestType.attachments_required)
            };
        },
        showAttachmentField() {
            return Boolean(this.selectedRequestType && this.canFillRequestDetails && this.attachmentConfig.enabled);
        },
        attachmentFieldLabel() {
            return this.attachmentConfig.required ? 'Вложения к описанию *' : 'Вложения к описанию';
        },
        attachmentFieldState() {
            if (!this.attachmentConfig.required) {
                return null;
            }

            return Array.isArray(this.form.attachments) && this.form.attachments.length > 0;
        },
        isDeadlineSelected() {
            return Boolean(this.form.deadline);
        },
        isBuildingProcessReady() {
            return !this.requiresBuildingProcess || Boolean(this.form.building_process_id);
        },
        canFillRequestDetails() {
            return this.isDeadlineSelected && this.isBuildingProcessReady;
        },
        requestDetailsPrerequisitesTitle() {
            if (this.requiresBuildingProcess) {
                return 'Сначала заполните обязательные поля';
            }

            return 'Сначала выберите срок';
        },
        requestDetailsPrerequisitesDescription() {
            if (this.requiresBuildingProcess) {
                return this.hasItemCatalog
                    ? 'После выбора срока и строительного процесса откроются позиции, описание и вложения. Так черновик начнет сохраняться до заполнения таблицы.'
                    : 'После выбора срока и строительного процесса откроется описание заявки. Так черновик начнет сохраняться до заполнения подробностей.';
            }

            return this.hasItemCatalog
                ? 'После выбора срока откроются позиции, описание и вложения. Так черновик начнет сохраняться до заполнения таблицы.'
                : 'После выбора срока откроется описание заявки. Так черновик начнет сохраняться до заполнения подробностей.';
        },
        requestDetailsPrerequisites() {
            const items = [
                {
                    key: 'deadline',
                    title: this.isDeadlineSelected ? 'Срок выбран' : 'Выберите срок',
                    description: this.isDeadlineSelected
                        ? `Срок выполнения: ${this.formatDateForUser(this.form.deadline)}`
                        : 'Это поле находится выше, справа от приоритета.',
                    icon: this.isDeadlineSelected ? 'fas fa-check-circle' : 'far fa-calendar-alt',
                    done: this.isDeadlineSelected
                }
            ];

            if (this.requiresBuildingProcess) {
                items.push({
                    key: 'building-process',
                    title: this.isBuildingProcessReady ? 'Строительный процесс выбран' : 'Выберите строительный процесс',
                    description: this.isBuildingProcessReady && this.selectedBuildingProcess
                        ? this.selectedBuildingProcess.text
                        : 'Это объект или процесс, для которого создается заявка.',
                    icon: this.isBuildingProcessReady ? 'fas fa-check-circle' : 'fas fa-hard-hat',
                    done: this.isBuildingProcessReady
                });
            }

            return items;
        },
        descriptionFieldLabel() {
            return this.isDescriptionRequired ? 'Описание *' : 'Описание';
        },
        descriptionFieldState() {
            if (!this.isDescriptionRequired) {
                return null;
            }

            return Boolean(this.form.description && this.form.description.trim());
        },
        requiredAttachmentConfig() {
            const workflowConfig = this.selectedRequestType && this.selectedRequestType.workflow_config
                ? this.selectedRequestType.workflow_config
                : {};
            const config = workflowConfig.requiredAttachments || workflowConfig.required_attachments || {};
            return {
                enabled: config.enabled === true,
                rules: Array.isArray(config.rules) ? config.rules : []
            };
        },
        requiredAttachmentRulesForCreate() {
            if (!this.requiredAttachmentConfig.enabled) {
                return [];
            }

            return this.requiredAttachmentConfig.rules
                .map((rule, index) => this.normalizeRequiredAttachmentRule(rule, index))
                .filter(rule => rule.active &&
                    this.isRequiredAttachmentRuleEnabledForRequest(rule) &&
                    (rule.requiredFor.includes('create') || rule.requiredFor.includes('submit')));
        },
        requiredAttachmentChoiceRulesForCreate() {
            if (!this.requiredAttachmentConfig.enabled) {
                return [];
            }

            return this.requiredAttachmentConfig.rules
                .map((rule, index) => this.normalizeRequiredAttachmentRule(rule, index))
                .filter(rule => rule.active && rule.initiatorChoice);
        },
        requiredAttachmentsValidForCreate() {
            return this.requiredAttachmentRulesForCreate.every(rule => {
                return this.getRequiredAttachmentCount(rule) >= rule.minCount;
            });
        },
        filteredMaterials() {
            if (!this.materialSearch) return this.availableMaterials;
            const search = this.materialSearch.toLowerCase();
            return this.availableMaterials.filter(material => 
                material.name.toLowerCase().includes(search)
            );
        },
        mechanicMaterialSelectorUsedIds() {
            if (!this.isMechanicAssetCatalog) {
                return [];
            }
            const materials = Array.isArray(this.form.selectedMaterials)
                ? this.form.selectedMaterials
                : [];
            return Array.from(new Set(materials
                .filter(material => material && material.isCustom !== true)
                .map(material => this.toPositiveInteger(material.catalog_item_id || material.id))
                .filter(Boolean)));
        },
        deadlinePolicyResult() { return deadlinePolicy(this.selectedRequestType || {}, this.form); },
        showUrgencyComment() {
            return this.canFillRequestDetails && this.deadlinePolicyResult.reasonRequired;
        },
        isDeadlineViolated() { return this.deadlinePolicyResult.deadlineUrgent; },
        urgencyReasonText() {
            return this.form.priority === 'urgent' ? 'Выбран срочный приоритет. Перенос даты сам по себе его не снимает.' :
                'Срок меньше регламентного (' + this.deadlinePolicyResult.requiredDays + ' дн.). Обычный срок — с ' +
                this.deadlinePolicyResult.minimumDate.split('-').reverse().join('.') + '.';
        },
        hasValidCustomItemNames() {
            return (this.form.selectedMaterials || []).every(material =>
                !(material.isCustom === true || material.is_custom === true) ||
                Boolean(String(material.name || material.item_name || '').trim())
            );
        },
        hasValidQuantities() {
            // Проверяем что у всех материалов указано количество > 0
            if (!this.form.selectedMaterials || this.form.selectedMaterials.length === 0) {
                return false;
            }
            return this.form.selectedMaterials.every(m => m.quantity && m.quantity > 0);
        },
        hasValidFuelCardRecipients() {
            if (!this.fuelCardNeedsRecipient) {
                return true;
            }
            if (this.isFuelCardIssueToRecipient) {
                return Boolean(this.fuelCardRecipientUserId);
            }
            return Array.isArray(this.form.selectedMaterials) &&
                this.form.selectedMaterials.length > 0 &&
                this.form.selectedMaterials.every(material => Boolean(material.target_user_id));
        },
        hasValidFuelCardLimitsSelection() {
            if (!this.fuelCardLimitsRequired) {
                return true;
            }
            return Array.isArray(this.form.selectedMaterials) &&
                this.form.selectedMaterials.length > 0 &&
                this.form.selectedMaterials.every(material => this.hasValidFuelLimits(material));
        },
        hasValidMechanicAssetSelection() {
            if (!this.isMechanicAssetCatalog) {
                return true;
            }
            if (!Array.isArray(this.form.selectedMaterials) || this.form.selectedMaterials.length === 0) {
                return false;
            }
            if (this.isMechanicAssetCatalogOnObject && !this.form.building_process_id) {
                return false;
            }

            return this.form.selectedMaterials.every(material => {
                if (!material || (!material.catalog_item_id && !String(material.name || material.item_name || '').trim())) {
                    return false;
                }
                if (this.isMechanicQuantityMaterial(material)) {
                    if (!material.quantity || Number(material.quantity) <= 0) {
                        return false;
                    }
                    if (!String(material.unit_of_measurement || material.unit || '').trim()) {
                        return false;
                    }
                }
                if (this.mechanicAssetNeedsTargetUser && !this.getMechanicMaterialTargetUserId(material)) {
                    return false;
                }
                if (this.mechanicAssetRequiresHeaderObject && !this.form.building_process_id) {
                    return false;
                }
                if (this.mechanicAssetNeedsWarehouse && !material.target_warehouse_id) {
                    return false;
                }
                if (this.mechanicAssetNeedsRental) {
                    if (!String(material.rental_counterparty || '').trim()) {
                        return false;
                    }
                    if (!material.rental_operator_user_id) {
                        return false;
                    }
                }
                return true;
            });
        },
        isItemCatalogSelectionValid() {
            if (!this.hasItemCatalog) {
                return true;
            }
            if (this.isFuelCardIssueToRecipient) {
                return this.hasValidFuelCardRecipients && this.hasValidFuelCardLimitsSelection;
            }
            if (!this.form.selectedMaterials || this.form.selectedMaterials.length === 0) {
                return false;
            }
            if (!this.hasValidCustomItemNames) {
                return false;
            }
            if (this.isMechanicAssetCatalog) {
                return this.hasValidMechanicAssetSelection;
            }
            if (!this.hasValidQuantities) {
                return false;
            }
            if (!this.hasValidFuelCardLimitsSelection) {
                return false;
            }
            return this.hasValidFuelCardRecipients;
        },
        isFormValid() {
            // Базовые обязательные поля
            if (!this.form.request_type_id) return false;
            if (!this.form.deadline) return false;
            if (this.isDescriptionRequired && (!this.form.description || !this.form.description.trim())) return false;
            
            // Название заявки (если требуется)
            if (this.selectedRequestType && this.selectedRequestType.requires_title) {
                if (!this.form.title || !this.form.title.trim()) return false;
            }
            
            // Причина срочности (если показывается поле)
            if (this.showUrgencyComment) {
                if (!this.form.urgency_comment || !this.form.urgency_comment.trim()) return false;
            }

            if (!this.areRequiredCustomFieldsFilled()) return false;
            
            // Поля для заявок со справочником позиций
            if (this.requiresBuildingProcess && !this.form.building_process_id) return false;

            if (this.attachmentConfig.required && (!this.form.attachments || !this.form.attachments.length) &&
                !(this.form.recurrence && this.recurrenceInheritedFiles.some(file => !file.file_kind))) return false;
            if (!this.requiredAttachmentsValidForCreate) return false;

            if (this.hasItemCatalog) {
                if (!this.isItemCatalogSelectionValid) return false;
            }
            
            return true;
        },
        isFormValidForDraft() {
            // Для черновика требуем только базовые поля
            if (!this.form.request_type_id) return false;
            if (!this.form.deadline) return false;
            if (this.requiresBuildingProcess && !this.form.building_process_id) return false;
            
            return true;
        },
        filesWithPreview() {
            if (!this.form.attachments || !this.form.attachments.length) return [];
            
            return this.form.attachments.map((file, index) => {
                const previewUrl = this.getLocalFilePreviewUrl(file);

                const isImage = isViewerImageFile(file);

                return {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    index,
                    previewUrl: previewUrl,
                    isImage: isImage,
                    iconClass: fileIconClass(file),
                    file: file
                };
            });
        },
        attachmentViewerFiles() {
            return this.filesWithPreview.map(file => ({
                id: this.getFileKey(file.file, file.index || 0),
                name: file.name,
                url: file.previewUrl,
                downloadUrl: file.previewUrl,
                thumbnailUrl: file.isImage ? file.previewUrl : null,
                mimeType: file.type,
                size: file.size,
                isImage: file.isImage,
                raw: file.file
            }));
        },
        lateApproversText() {
            // Проверяем наличие и валидность конфигурации дополнительных согласующих
            if (!this.selectedRequestType || 
                !this.selectedRequestType.late_approval_config || 
                !this.selectedRequestType.late_approval_config.enabled ||
                !this.selectedRequestType.late_approval_config.approvers ||
                !this.selectedRequestType.late_approval_config.approvers.participants ||
                !Array.isArray(this.selectedRequestType.late_approval_config.approvers.participants)) {
                return '';
            }
            
            const participants = this.selectedRequestType.late_approval_config.approvers.participants;
            if (participants.length === 0) {
                return '';
            }
            
            const approversList = [];
            
            // Проходим по массиву participants
            for (const participant of participants) {
                if (!participant || !participant.type) continue;
                
                let names = [];
                
                switch (participant.type) {
                    case 'specific_users':
                        if (participant.user_ids && Array.isArray(participant.user_ids) && participant.user_ids.length > 0) {
                            names = participant.user_ids
                                .map(userId => this.getUserNameById(userId))
                                .filter(Boolean);
                        }
                        break;
                        
                    case 'department':
                        if (participant.department_id) {
                            names = this.getDepartmentUserNames(participant.department_id);
                        }
                        break;
                        
                    case 'all':
                        names = this.activeUserNames;
                        break;
                        
                    case 'object_role':
                        if (participant.roles && Array.isArray(participant.roles) && participant.roles.length > 0) {
                            names = this.getObjectRoleUserNames(participant.roles);
                        }
                        break;
                }
                
                approversList.push(...names);
            }
            
            // Убираем дубликаты
            const uniqueApprovers = [...new Set(approversList)];
            
            if (uniqueApprovers.length === 0) {
                return '';
            }
            
            // Форматируем список с правильной пунктуацией
            return this.formatApproversList(uniqueApprovers);
        },
        activeUserNames() {
            return (this.$store.state.users || [])
                .filter(user => this.isActiveHumanUser(user))
                .map(user => this.formatUserName(user))
                .filter(Boolean);
        },
        isTelegramWebApps() {
            return this.$store.getters.isTelegramWebApps;
        }
    },
    methods: {
        toggleRecurrence(enabled) {
            if (!enabled) { this.form.recurrence = null; return; }
            this.form.recurrence = {schedule: {kind: 'cron', cron: '0 0 * * *', startDate: deadlineParts(this.form.deadline).date || new Date(Date.now() + 10800000).toISOString().slice(0, 10), endDate: null,
                leadDays: recurrenceDomain.config(this.selectedRequestType).leadDays, dates: []}};
            this.setRecurrenceSchedule(this.form.recurrence.schedule);
        },
        setRecurrenceSchedule(schedule) {
            this.form.recurrence = {...this.form.recurrence, schedule};
            this.form.deadline = schedule.kind === 'dates' ? (schedule.dates || []).slice().sort()[0] || null : schedule.startDate || null;
        },
        goBackToList(options = {}) {
            const method = options && options.replace ? 'replace' : 'push';
            if (this.isLinkedRequestFlow && this.linkedSourceRequestId) {
                return this.$router[method]({ path: `/requests/${this.linkedSourceRequestId}` }).catch(() => {});
            }

            return this.$router[method]({ path: '/requests' }).catch(() => {});
        },
        getTelegramBackButton() {
            return window.Telegram
                && window.Telegram.WebApp
                && window.Telegram.WebApp.BackButton
                ? window.Telegram.WebApp.BackButton
                : null;
        },
        setupTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === 'undefined') {
                return;
            }

            this.telegramBackButtonHandler = () => {
                this.goBackToList({ replace: true });
            };

            const backButton = this.getTelegramBackButton();
            if (backButton) {
                if (typeof backButton.onClick === 'function') {
                    backButton.onClick(this.telegramBackButtonHandler);
                }
                if (typeof backButton.show === 'function') {
                    backButton.show();
                }
            }

            window.addEventListener('popstate', this.handleTelegramHistoryBack);
            this.pushTelegramHistorySentinel();
        },
        teardownTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === 'undefined') {
                return;
            }

            const backButton = this.getTelegramBackButton();
            if (
                backButton
                && this.telegramBackButtonHandler
                && typeof backButton.offClick === 'function'
            ) {
                backButton.offClick(this.telegramBackButtonHandler);
            }
            if (backButton && typeof backButton.hide === 'function') {
                backButton.hide();
            }

            window.removeEventListener('popstate', this.handleTelegramHistoryBack);
            this.telegramBackButtonHandler = null;
        },
        pushTelegramHistorySentinel() {
            try {
                const state = window.history.state || {};
                if (state && state.createRequestTelegramBack) {
                    return;
                }
                window.history.pushState(
                    {
                        ...state,
                        createRequestTelegramBack: true
                    },
                    document.title,
                    window.location.href
                );
            } catch (error) {
                console.error('Не удалось подготовить Telegram back navigation:', error);
            }
        },
        handleTelegramHistoryBack() {
            if (
                !this.isTelegramWebApps
                || this.telegramHistoryBackInProgress
                || this.$route.name !== 'create-request'
            ) {
                return;
            }

            this.telegramHistoryBackInProgress = true;
            this.goBackToList({ replace: true })
                .finally(() => {
                    this.telegramHistoryBackInProgress = false;
                });
        },
        formatDateForUser(value) {
            if (!value) {
                return '';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },
        toPositiveInteger(value) {
            const numericValue = Number(value);
            return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
        },
        getLinkedRequestQuery() {
            const query = this.$route.query || {};
            return {
                sourceRequestId: this.toPositiveInteger(query.sourceRequestId || query.source_request_id),
                linkRuleKey: String(query.linkRuleKey || query.link_rule_key || '').trim(),
                requestTypeId: this.toPositiveInteger(query.requestTypeId || query.request_type_id)
            };
        },
        hasLinkedRequestQuery() {
            const query = this.getLinkedRequestQuery();
            return Boolean(query.sourceRequestId || query.linkRuleKey);
        },
        isCustomFieldValueEmpty(value) {
            if (value === undefined || value === null) return true;
            if (typeof value === 'string') return value.trim() === '';
            if (Array.isArray(value)) return value.length === 0;
            if (typeof value === 'object' && (
                Object.prototype.hasOwnProperty.call(value, 'from') ||
                Object.prototype.hasOwnProperty.call(value, 'to') ||
                Object.prototype.hasOwnProperty.call(value, 'start') ||
                Object.prototype.hasOwnProperty.call(value, 'end') ||
                Object.prototype.hasOwnProperty.call(value, 'date_from') ||
                Object.prototype.hasOwnProperty.call(value, 'date_to')
            )) {
                return ![
                    value.from,
                    value.to,
                    value.start,
                    value.end,
                    value.date_from,
                    value.date_to,
                    value.dateFrom,
                    value.dateTo
                ].some(item => item !== undefined && item !== null && String(item).trim() !== '');
            }
            if (typeof value === 'object') return Object.keys(value).length === 0;
            return false;
        },
        areRequiredCustomFieldsFilled() {
            return this.visibleCustomFieldDefinitions
                .filter(field => field.required === true && field.type !== 'checkbox')
                .every(field => !this.isCustomFieldValueEmpty(
                    this.form.custom_fields ? this.form.custom_fields[field.key] : undefined
                ));
        },
        getCustomFieldVisibilityCondition(field) {
            const condition = field && (
                field.visibility ||
                field.visibility_condition ||
                field.show_if ||
                field.showIf
            );
            if (!condition || typeof condition !== 'object' || condition.enabled === false) {
                return null;
            }
            const fieldKey = String(
                condition.field_key ||
                condition.fieldKey ||
                condition.source_field_key ||
                condition.sourceFieldKey ||
                ''
            ).trim();
            if (!fieldKey) {
                return null;
            }
            return {
                fieldKey,
                operator: String(condition.operator || 'equals').trim() || 'equals',
                value: condition.value !== undefined ? condition.value : condition.values
            };
        },
        normalizeCustomFieldComparableToken(value) {
            if (value === undefined || value === null) return '';
            return String(value).trim().toLowerCase();
        },
        getCustomFieldComparableTokens(value) {
            if (this.isCustomFieldValueEmpty(value)) return [];
            if (Array.isArray(value)) {
                return value.reduce((result, item) => result.concat(this.getCustomFieldComparableTokens(item)), []);
            }
            if (typeof value === 'object') {
                return ['id', 'value', 'key', 'label', 'text', 'name']
                    .map(prop => value[prop])
                    .filter(item => item !== undefined && item !== null && item !== '')
                    .map(item => this.normalizeCustomFieldComparableToken(item))
                    .filter(Boolean);
            }
            return [this.normalizeCustomFieldComparableToken(value)].filter(Boolean);
        },
        getCustomFieldExpectedTokens(value) {
            if (Array.isArray(value)) {
                return value.reduce((result, item) => result.concat(this.getCustomFieldComparableTokens(item)), []);
            }
            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map(item => this.normalizeCustomFieldComparableToken(item))
                    .filter(Boolean);
            }
            return this.getCustomFieldComparableTokens(value);
        },
        isCustomFieldVisibleForValues(field, values) {
            const condition = this.getCustomFieldVisibilityCondition(field);
            if (!condition) return true;
            const sourceValue = Object.prototype.hasOwnProperty.call(values || {}, condition.fieldKey)
                ? values[condition.fieldKey]
                : undefined;
            const sourceFilled = !this.isCustomFieldValueEmpty(sourceValue);
            const sourceTokens = this.getCustomFieldComparableTokens(sourceValue);
            const expectedTokens = this.getCustomFieldExpectedTokens(condition.value);
            const matched = sourceTokens.some(token => expectedTokens.indexOf(token) !== -1);
            if (condition.operator === 'filled') return sourceFilled;
            if (condition.operator === 'empty') return !sourceFilled;
            if (condition.operator === 'not_equals' || condition.operator === 'not_in') {
                return sourceFilled && !matched;
            }
            return matched;
        },
        getVisibleCustomFieldDefinitions(values) {
            const source = values || {};
            const visibleValues = {};
            return this.customFieldDefinitions.filter(field => {
                const visible = this.isCustomFieldVisibleForValues(field, visibleValues);
                if (visible && Object.prototype.hasOwnProperty.call(source, field.key)) {
                    visibleValues[field.key] = source[field.key];
                } else if (visible && field.type === 'checkbox') {
                    visibleValues[field.key] = false;
                }
                return visible;
            });
        },
        buildCreateRequestPayload(extra = {}) {
            const payload = {
                ...this.form,
                ...extra
            };
            if (!this.isReportAnnulment) delete payload.report_annulment;

            if (payload.recurrence) {
                if (!this.recurrenceAvailable) payload.recurrence = null;
                else payload.recurrence = {...payload.recurrence, schedule: recurrenceDomain.normalizeSchedule(payload.recurrence.schedule)};
            }
            if (this.sourceRequestLoaded && this.$route.query.copyFrom && (this.form.selectedMaterials || []).some(item => item.ppe_data && item.ppe_source_item_id)) {
                payload.copy_source_request_id = this.$route.query.copyFrom;
            }

            if (this.isLinkedRequestFlow) {
                payload.source_request_id = this.linkedSourceRequestId;
                payload.link_rule_key = this.linkedRuleKey;
            }

            return payload;
        },
        buildPlainCreateRequestPayload(requestData) {
            const plainRequestData = { ...requestData };
            delete plainRequestData.attachments;
            delete plainRequestData.requiredAttachments;

            return plainRequestData;
        },
        collectCreateRequestAttachments() {
            const generalAttachments = Array.isArray(this.form.attachments)
                ? this.form.attachments
                : [];
            const requiredAttachments = this.requiredAttachmentRulesForCreate.reduce((result, rule) => {
                this.getRequiredAttachmentFiles(rule).forEach(file => {
                    result.push({ file, file_kind: rule.key });
                });
                return result;
            }, []);

            return {
                generalAttachments,
                requiredAttachments
            };
        },
        async uploadCreatedRequestAttachments(requestId, generalAttachments, requiredAttachments) {
            if (generalAttachments.length) {
                await this.$store.dispatch('requests_uploadRequestFiles', {
                    id: requestId,
                    files: generalAttachments
                });
            }

            for (const { file, file_kind } of requiredAttachments) {
                await this.$store.dispatch('requests_uploadRequiredAttachment', {
                    id: requestId,
                    fileKind: file_kind,
                    files: [file]
                });
            }
        },
        async createRequestFromPayload(requestData) {
            const plainRequestData = this.buildPlainCreateRequestPayload(requestData);
            const { generalAttachments, requiredAttachments } = this.collectCreateRequestAttachments();

            if (!generalAttachments.length && !requiredAttachments.length) {
                return this.$store.dispatch('requests_createRequest', plainRequestData);
            }

            const requestedStatus = plainRequestData.status || 'draft';
            const createPayload = {
                ...plainRequestData,
                status: 'draft'
            };
            const created = await this.$store.dispatch('requests_createRequest', createPayload);
            const requestId = created && created.id;

            if (!requestId) {
                throw new Error('Заявка создана, но сервер не вернул ID');
            }

            await this.uploadCreatedRequestAttachments(requestId, generalAttachments, requiredAttachments);

            if (requestedStatus === 'submitted') {
                const submitted = await this.$store.dispatch('requests_submitRequest', requestId);
                return {
                    ...created,
                    ...(submitted || {}),
                    id: requestId
                };
            }

            return created;
        },
        async initializeRequestFromQuery() {
            const query = this.$route.query || {};
            const typeId = this.toPositiveInteger(query.requestTypeId || query.request_type_id);
            if (!typeId) return false;

            try {
                const type = await this.ensureRequestTypeAvailable(typeId);
                if (!type || type.is_active === false) throw new Error('Тип заявки недоступен.');
                this.form.request_type_id = type.value;
                this.syncSelectedCategoryFromType();
                // Initialize the regular form first: changing type resets the object.
                await this.onRequestTypeChange({skipDraftCheck: true});
                const processId = this.toPositiveInteger(query.buildingProcessId);
                if (processId && this.showsBuildingProcess) {
                    if (!this.buildingProcesses.some(row => Number(row.value) === processId)) {
                        throw new Error('Объект из выбранных отчётов недоступен для этой заявки.');
                    }
                    this.form.building_process_id = processId;
                }
                // A fresh report selection must not be replaced by an unrelated saved draft.
                if (!(this.isReportAnnulment && query.reports)) await this.checkForDraft();
            } catch (error) {
                this.$bvToast.toast(error.message || 'Не удалось заполнить заявку из выбранных отчётов.', {
                    title: 'Создание заявки', variant: 'danger', solid: true
                });
            }
            return true;
        },
        async ensureRequestTypeAvailable(requestTypeId) {
            const normalizedTypeId = this.toPositiveInteger(requestTypeId);
            if (!normalizedTypeId) {
                return null;
            }

            let requestType = this.requestTypes.find(type => Number(type.value) === normalizedTypeId);
            if (requestType) {
                return requestType;
            }

            const rawRequestType = await this.$store.dispatch('requests_getRequestType', normalizedTypeId);
            if (!rawRequestType) {
                return null;
            }

            const exists = this.extraRequestTypes.some(type => Number(type.id) === Number(rawRequestType.id));
            if (!exists) {
                this.extraRequestTypes = [rawRequestType].concat(this.extraRequestTypes);
                await this.$nextTick();
            }

            return this.requestTypes.find(type => Number(type.value) === normalizedTypeId) || null;
        },
        normalizeItemCatalogScope(itemCatalog, catalogType) {
            if (catalogType !== 'mechanic_assets') {
                return 'all';
            }
            const source = itemCatalog && typeof itemCatalog === 'object' ? itemCatalog : {};
            const scope = String(
                source.scope ||
                source.catalog_scope ||
                source.catalogScope ||
                source.visibility ||
                source.mechanic_assets_scope ||
                source.mechanicAssetsScope ||
                ''
            ).trim();
            return scope === 'mechanic_assets_on_object' ? scope : 'all';
        },
        getItemCatalogConfig(requestType) {
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const itemCatalog = workflowConfig && workflowConfig.itemCatalog ? workflowConfig.itemCatalog : null;
            const hasExplicitItemCatalogConfig = itemCatalog && typeof itemCatalog === 'object';

            if (hasExplicitItemCatalogConfig && itemCatalog.enabled && itemCatalog.type) {
                const defaultLabels = {
                    employees: 'Сотрудники',
                    materials: 'Материалы',
                    fuel_cards: 'Топливные карты',
                    mechanic_assets: 'Техника'
                };
                return {
                    enabled: true,
                    type: itemCatalog.type,
                    label: itemCatalog.label || defaultLabels[itemCatalog.type] || 'Позиции',
                    scope: this.normalizeItemCatalogScope(itemCatalog, itemCatalog.type),
                    allow_custom_items: itemCatalog.type === 'employees' &&
                        (
                            itemCatalog.allow_custom_items === true ||
                            itemCatalog.allowCustomItems === true ||
                            itemCatalog.allow_manual_items === true ||
                            itemCatalog.allowManualItems === true
                        )
                };
            }

            if (hasExplicitItemCatalogConfig) {
                return {
                    enabled: false,
                    type: null,
                    label: 'Позиции',
                    scope: 'all',
                    allow_custom_items: false
                };
            }

            if (requestType && requestType.category === 'materials') {
                return {
                    enabled: true,
                    type: 'materials',
                    label: 'Материалы',
                    scope: 'all',
                    allow_custom_items: true
                };
            }

            if (requestType && requestType.category === 'fuelCards') {
                return {
                    enabled: true,
                    type: 'fuel_cards',
                    label: 'Топливные карты',
                    scope: 'all',
                    allow_custom_items: false
                };
            }

            if (requestType && requestType.category === 'mechanics') {
                return {
                    enabled: true,
                    type: 'mechanic_assets',
                    label: 'Техника',
                    scope: 'all',
                    allow_custom_items: false
                };
            }

            return {
                enabled: false,
                type: null,
                label: 'Позиции',
                scope: 'all',
                allow_custom_items: false
            };
        },
        getBuildingProcessConfig(requestType) {
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const itemCatalog = workflowConfig && workflowConfig.itemCatalog ? workflowConfig.itemCatalog : null;
            const source = workflowConfig.buildingProcess || workflowConfig.building_process;
            const itemCatalogRequiresBuildingProcess = Boolean(
                itemCatalog &&
                itemCatalog.enabled === true &&
                this.normalizeItemCatalogScope(itemCatalog, itemCatalog.type) === 'mechanic_assets_on_object'
            );

            if (source && typeof source === 'object' && !Array.isArray(source)) {
                const required = source.required === true ||
                    source.isRequired === true ||
                    source.required_for_create === true;
                const enabled = source.enabled !== undefined
                    ? source.enabled === true
                    : (source.show !== undefined
                        ? source.show === true
                        : required);

                return {
                    enabled: enabled || required || itemCatalogRequiresBuildingProcess,
                    required: required || itemCatalogRequiresBuildingProcess
                };
            }

            if (workflowConfig.requiresBuildingProcess !== undefined) {
                const required = Boolean(workflowConfig.requiresBuildingProcess);
                return {
                    enabled: required || itemCatalogRequiresBuildingProcess,
                    required: required || itemCatalogRequiresBuildingProcess
                };
            }

            if (workflowConfig.requires_building_process !== undefined) {
                const required = Boolean(workflowConfig.requires_building_process);
                return {
                    enabled: required || itemCatalogRequiresBuildingProcess,
                    required: required || itemCatalogRequiresBuildingProcess
                };
            }

            if (itemCatalog && itemCatalog.requires_building_process !== undefined) {
                const required = Boolean(itemCatalog.requires_building_process);
                return {
                    enabled: required || itemCatalogRequiresBuildingProcess,
                    required: required || itemCatalogRequiresBuildingProcess
                };
            }

            if (itemCatalogRequiresBuildingProcess) {
                return { enabled: true, required: true };
            }

            const required = Boolean(requestType && requestType.category === 'materials');
            return { enabled: required, required };
        },
        getTruncatedRequestTypeDescription(description) {
            const text = String(description || '').replace(/\s+/g, ' ').trim();
            const maxLength = 96;

            if (text.length <= maxLength) {
                return text;
            }

            return `${text.substring(0, maxLength).trimEnd()}...`;
        },
        toFileArray(files) {
            if (Array.isArray(files)) {
                return files;
            }
            if (files && typeof files.length === 'number') {
                return Array.from(files);
            }
            return files ? [files] : [];
        },
        onDescriptionFilesChange(files) {
            const list = this.toFileArray(files);
            // Объединяем новые файлы с уже выбранными, исключая дубликаты по name+size+lastModified
            const existing = this.form.attachments;
            const byKey = f => `${f.name}|${f.size}|${f.lastModified}`;
            const map = new Map(existing.map(f => [byKey(f), f]));
            list.forEach(f => {
                const key = byKey(f);
                if (!map.has(key)) map.set(key, f);
            });
            this.form.attachments = Array.from(map.values());
            // очищаем input-модель, чтобы можно было выбрать тот же файл снова при необходимости
            this.$nextTick(() => { this.descriptionFilesInput = null; });
        },
        normalizeRequiredAttachmentRule(rule, index) {
            return normalizeRequiredAttachmentRuleConfig(rule, index);
        },
        isRequiredAttachmentRuleEnabledForRequest(rule) {
            return isRequiredAttachmentRuleEnabledByChoices(rule, this.form.requiredAttachmentChoices);
        },
        initializeRequiredAttachmentChoices() {
            const current = this.form.requiredAttachmentChoices || {};
            const choices = {};
            this.requiredAttachmentChoiceRulesForCreate.forEach(rule => {
                choices[rule.key] = current[rule.key] === true;
            });
            this.$set(this.form, 'requiredAttachmentChoices', choices);
        },
        setRequiredAttachmentChoice(key, value) {
            if (!this.form.requiredAttachmentChoices) {
                this.$set(this.form, 'requiredAttachmentChoices', {});
            }
            this.$set(this.form.requiredAttachmentChoices, key, value === true);
        },
        getRequiredAttachmentFiles(rule) {
            const files = this.form.requiredAttachments && this.form.requiredAttachments[rule.key];
            return Array.isArray(files) ? files : [];
        },
        getRequiredAttachmentCount(rule) {
            return this.getRequiredAttachmentFiles(rule).length + (this.form.recurrence
                ? this.recurrenceInheritedFiles.filter(file => file.file_kind === rule.key).length : 0);
        },
        getRequiredAttachmentLabel(rule) {
            return rule.minCount > 1
                ? `${rule.label} * (${rule.minCount} файл.)`
                : `${rule.label} *`;
        },
        getRequiredAttachmentState(rule) {
            return this.getRequiredAttachmentCount(rule) >= rule.minCount;
        },
        getRequiredAttachmentMissingText(rule) {
            return rule.minCount > 1
                ? `Прикрепите минимум ${rule.minCount} файл.`
                : 'Прикрепите файл.';
        },
        onRequiredAttachmentFilesChange(rule, files) {
            const list = this.toFileArray(files);
            const existing = this.getRequiredAttachmentFiles(rule);
            const byKey = f => `${f.name}|${f.size}|${f.lastModified}`;
            const map = new Map(existing.map(f => [byKey(f), f]));
            list.forEach(f => {
                const key = byKey(f);
                if (!map.has(key)) map.set(key, f);
            });
            this.$set(this.form.requiredAttachments, rule.key, Array.from(map.values()));
            this.$nextTick(() => {
                this.$set(this.requiredAttachmentInputs, rule.key, null);
            });
        },
        removeRequiredAttachmentFile(rule, index) {
            const files = [...this.getRequiredAttachmentFiles(rule)];
            const file = files[index];
            if (file && this.filePreviewUrls.has(file)) {
                const url = this.filePreviewUrls.get(file);
                URL.revokeObjectURL(url);
                this.filePreviewUrls.delete(file);
            }
            files.splice(index, 1);
            this.$set(this.form.requiredAttachments, rule.key, files);
        },
        removeDescriptionFile(index) {
            const file = this.form.attachments[index];
            // Освобождаем URL перед удалением файла
            if (file && this.filePreviewUrls.has(file)) {
                const url = this.filePreviewUrls.get(file);
                URL.revokeObjectURL(url);
                this.filePreviewUrls.delete(file);
            }
            this.form.attachments.splice(index, 1);
        },
        openFilePreview(file) {
            if (!file || !file.previewUrl) {
                return;
            }
            if (!this.$fileViewer) {
                window.open(file.previewUrl, '_blank');
                return;
            }
            this.$fileViewer.open(this.attachmentViewerFiles, {
                startId: this.getFileKey(file.file, file.index || 0),
                source: `request-create-attachments`,
                pushHistory: false
            });
        },
        getLocalFilePreviewUrl(file) {
            let previewUrl = this.filePreviewUrls.get(file);
            if (!previewUrl && file instanceof File) {
                previewUrl = URL.createObjectURL(file);
                this.filePreviewUrls.set(file, previewUrl);
            }
            return previewUrl;
        },
        getLocalFileIconClass(file) {
            return fileIconClass(file);
        },
        getRequiredAttachmentViewerFiles(rule) {
            return this.getRequiredAttachmentFiles(rule).map((file, index) => {
                const previewUrl = this.getLocalFilePreviewUrl(file);
                const image = isViewerImageFile(file);
                return {
                    id: this.getFileKey(file, index),
                    name: file.name || `Файл ${index + 1}`,
                    url: previewUrl,
                    downloadUrl: previewUrl,
                    thumbnailUrl: image ? previewUrl : null,
                    mimeType: file.type,
                    size: file.size,
                    isImage: image,
                    raw: file
                };
            });
        },
        openRequiredAttachmentPreview(rule, file, index) {
            const previewUrl = this.getLocalFilePreviewUrl(file);
            if (!previewUrl) {
                return;
            }
            if (!this.$fileViewer) {
                window.open(previewUrl, `_blank`);
                return;
            }
            this.$fileViewer.open(this.getRequiredAttachmentViewerFiles(rule), {
                startId: this.getFileKey(file, index),
                source: `request-create-required-attachments`,
                pushHistory: false
            });
        },
        getFileKey(file, index) {
            // Создаем уникальный ключ на основе свойств файла и индекса
            // Используем комбинацию для гарантии уникальности
            const name = file.name || 'unnamed';
            const size = file.size || 0;
            const lastModified = file.lastModified || Date.now();
            return `file_${name}_${size}_${lastModified}_${index}`;
        },
        formatFileSize(size) {
            return formatViewerFileSize(size);
        },
        categoryCardClass(categoryValue) {
            return this.selectedRequestCategory === categoryValue
                ? 'request-choice-card--selected request-choice-card--category-selected shadow-sm'
                : 'request-choice-card--idle';
        },
        typeCardClass(typeValue) {
            return this.form.request_type_id === typeValue
                ? 'request-choice-card--selected request-choice-card--type-selected shadow-sm'
                : 'request-choice-card--idle';
        },
        normalizeRequestTypeSearch(value) {
            return String(value || '').trim().toLowerCase();
        },
        requestCategoryMatchesSearch(category, query) {
            if (!category || !query) {
                return false;
            }
            return [
                category.text,
                category.description,
                category.value
            ].some(value => this.normalizeRequestTypeSearch(value).includes(query));
        },
        requestTypeMatchesSearch(type, query) {
            if (!type || !query) {
                return true;
            }
            const category = REQUEST_CATEGORIES.find(item => item.value === type.normalizedCategory);
            return [
                type.text,
                type.description,
                type.category,
                category && category.text,
                category && category.description
            ].some(value => this.normalizeRequestTypeSearch(value).includes(query));
        },
        clearRequestTypeSearch() {
            this.requestTypeSearch = '';
        },
        toggleRequestTypePicker() {
            if (!this.form.request_type_id || this.isLinkedRequestFlow) {
                return;
            }

            this.requestTypePickerExpanded = !this.requestTypePickerExpanded;
        },
        expandRequestTypePicker() {
            this.requestTypePickerExpanded = true;
        },
        collapseRequestTypePicker() {
            if (this.form.request_type_id) {
                this.requestTypePickerExpanded = false;
            }
        },
        expandRequestCategoryStep() {
            this.requestCategoryStepCollapsed = false;
            this.$nextTick(() => {
                const categoryStep = this.$el && this.$el.querySelector
                    ? this.$el.querySelector('.request-category-step')
                    : null;
                if (categoryStep && typeof categoryStep.scrollIntoView === 'function') {
                    categoryStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },
        scrollToRequestTypeStepOnMobile() {
            const viewportWidth = window.innerWidth ||
                document.documentElement.clientWidth ||
                document.body.clientWidth ||
                0;
            if (viewportWidth > 575) {
                return;
            }

            const target = this.$refs.requestTypeStep;
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        async selectRequestCategory(categoryValue) {
            if (this.isLinkedRequestFlow) {
                return;
            }

            if (this.selectedRequestCategory === categoryValue) {
                this.requestCategoryStepCollapsed = true;
                this.expandRequestTypePicker();
                this.$nextTick(() => {
                    this.scrollToRequestTypeStepOnMobile();
                });
                return;
            }

            this.selectedRequestCategory = categoryValue;
            this.requestCategoryStepCollapsed = true;
            this.expandRequestTypePicker();

            if (this.form.request_type_id) {
                this.form.request_type_id = null;
                await this.onRequestTypeChange();
            }

            this.$nextTick(() => {
                this.scrollToRequestTypeStepOnMobile();
            });
        },
        async selectRequestType(type) {
            if (this.isLinkedRequestFlow) {
                return;
            }

            if (!type || this.form.request_type_id === type.value) {
                return;
            }

            this.form.request_type_id = type.value;
            this.selectedRequestCategory = type.normalizedCategory;
            await this.onRequestTypeChange();
            this.collapseRequestTypePicker();
        },
        syncSelectedCategoryFromType() {
            const requestType = this.selectedRequestType;
            this.selectedRequestCategory = requestType ? requestType.normalizedCategory : null;
            this.requestCategoryStepCollapsed = Boolean(requestType);
            this.requestTypePickerExpanded = !requestType;
        },
        async loadBuildingProcesses() {
            this.buildingProcessesLoading = true;
            try {
                // Используем новый API endpoint с фильтрацией по ролям
                // Передаем request_type_id для определения доступных ролей из initiator_config
                const requestTypeId = this.form.request_type_id || null;
                const processes = await this.$store.dispatch('requests_getAvailableBuildingProcesses', requestTypeId);
                
                const options = Array.isArray(processes) ? processes : [];
                this.buildingProcesses = options.map(op => ({
                    ...op,
                    value: op.building_process_id,
                    text: op.object_name
                }));
            } catch (e) {
                console.error('Ошибка загрузки процессов:', e);
                this.$bvToast && this.$bvToast.toast('Не удалось загрузить процессы', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                this.buildingProcesses = [];
            } finally {
                this.buildingProcessesLoading = false;
            }
        },
        async ensureMechanicAssetDictionaries() {
            if (!this.isMechanicAssetCatalog || this.mechanicAssetDictionariesLoaded) {
                return;
            }
            try {
                await this.$store.dispatch('mechanicAssets/fetchDictionaries');
                this.mechanicAssetDictionariesLoaded = true;
            } catch (error) {
                console.error('Ошибка загрузки справочников механиков:', error);
                this.$bvToast && this.$bvToast.toast('Не удалось загрузить справочники механиков', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        async onRequestTypeChange(options = {}) {
            // Сброс специальных полей при смене типа
            this.availableMaterials = [];
            this.loadedItemCatalogKey = null;
            this.materialSearch = '';
            this.showMechanicMaterialSelector = false;
            this.$set(this.form, 'custom_fields', {});

            if (!this.showsBuildingProcess) {
                this.form.building_process_id = null;
            }

            if (!this.hasItemCatalog) {
                this.form.selectedMaterials = [];
            }

            if (!this.isFuelCardIssueToRecipient) {
                this.fuelCardRecipientUserId = null;
            } else {
                this.form.selectedMaterials = [];
            }

            if (!this.isMechanicAssetCatalog) {
                this.mechanicDefaultTargetUserId = null;
                this.lastMechanicDefaultTargetUserId = null;
            }

            if (this.isMechanicAssetCatalog) {
                await this.ensureMechanicAssetDictionaries();
                if ((this.mechanicAssetRequiresHeaderObject || this.isMechanicAssetCatalogOnObject) && !this.buildingProcesses.length) {
                    await this.loadBuildingProcesses();
                }
            }

            if (!this.attachmentConfig.enabled) {
                this.form.attachments = [];
                this.descriptionFilesInput = null;
            }
            this.form.requiredAttachments = {};
            this.$set(this.form, 'requiredAttachmentChoices', {});
            this.initializeRequiredAttachmentChoices();
            this.requiredAttachmentInputs = {};

            if (this.showsBuildingProcess) {
                // Перезагружаем процессы с учетом нового типа заявки для фильтрации по ролям
                if (!options.keepBuildingProcess) {
                    this.form.building_process_id = null;
                }
                await this.loadBuildingProcesses();
            }
            
            // Проверяем наличие черновика для нового типа заявки
            if (this.form.request_type_id && !options.skipDraftCheck) {
                await this.checkForDraft();
            }
        },
        onBuildingProcessChange() {
            if (this.isMechanicAssetCatalogOnObject) {
                this.availableMaterials = [];
                this.loadedItemCatalogKey = null;
                this.materialSearch = '';
                this.showMaterialSelector = false;
                this.showMechanicMaterialSelector = false;
                this.form.selectedMaterials = [];
            }
        },
        sanitizeFuelLimits(value) {
            return sanitizeFuelLimitsValue(value);
        },
        cloneFuelLimits(value) {
            return cloneFuelLimitsValue(value);
        },
        getInitialFuelLimits(value) {
            const limits = this.cloneFuelLimits(value);
            return limits.length ? limits : [{ fuel_type_id: null, value: null }];
        },
        hasValidFuelLimits(material) {
            return this.sanitizeFuelLimits(material && material.fuel_limits).length > 0;
        },
        addFuelLimitRow(material) {
            if (!material) {
                return;
            }
            if (!Array.isArray(material.fuel_limits)) {
                this.$set(material, 'fuel_limits', []);
            }
            material.fuel_limits.push({ fuel_type_id: null, value: null });
        },
        removeFuelLimitRow(material, index) {
            if (!material || !Array.isArray(material.fuel_limits)) {
                return;
            }
            material.fuel_limits.splice(index, 1);
        },
        formatFuelLimits(value) {
            return formatFuelLimitsValue(value, this.fuelTypeById);
        },
        getMechanicMeterTypeText(value) {
            const option = this.mechanicAssetMeterTypeOptions.find(item => item.value === value);
            return option && option.value ? option.text.toLowerCase() : '';
        },
        setFuelLimitType(limit, value) {
            setFuelLimitTypeValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
        },
        setFuelLimitValue(limit, value) {
            setFuelLimitAmountValue(limit, value, (target, key, normalizedValue) => this.$set(target, key, normalizedValue));
        },
        getFuelLimitByIndexes(materialIndex, limitIndex) {
            const material = Array.isArray(this.form.selectedMaterials)
                ? this.form.selectedMaterials[materialIndex]
                : null;
            if (!material || !Array.isArray(material.fuel_limits)) {
                return null;
            }
            return material.fuel_limits[limitIndex] || null;
        },
        syncFuelLimitFormControls() {
            if (!this.$el || !this.fuelCardCanEditLimits) {
                return;
            }

            syncFuelLimitControls({
                root: this.$el,
                typeSelector: '[data-fuel-limit-type]',
                valueSelector: '[data-fuel-limit-value]',
                getLimit: control => {
                    const materialIndex = Number(control.getAttribute('data-fuel-limit-material-index'));
                    const limitIndex = Number(control.getAttribute('data-fuel-limit-index'));
                    return this.getFuelLimitByIndexes(materialIndex, limitIndex);
                },
                setter: (target, key, value) => this.$set(target, key, value)
            });
        },
        async flushPendingInputChanges() {
            this.syncFuelLimitFormControls();
            if (typeof document !== 'undefined' && document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
            }
            await this.$nextTick();
            await this.$nextTick();
            this.syncFuelLimitFormControls();
        },
        async loadMaterials() {
            if (this.isMechanicAssetCatalogOnObject && !this.form.building_process_id) {
                this.availableMaterials = [];
                this.loadedItemCatalogKey = null;
                return;
            }

            const catalogKey = [
                this.form.request_type_id || '',
                this.itemCatalogType || '',
                this.itemCatalogScope || 'all',
                this.isMechanicAssetCatalogOnObject ? (this.form.building_process_id || '') : ''
            ].join(':');
            if (this.availableMaterials.length > 0 && this.loadedItemCatalogKey === catalogKey) return; // Уже загружены
            if (this.isMechanicAssetCatalog) {
                await this.ensureMechanicAssetDictionaries();
            }

            this.materialsLoading = true;
            try {
                const catalogRequest = {
                    requestTypeId: this.form.request_type_id
                };
                if (this.isMechanicAssetCatalogOnObject) {
                    catalogRequest.buildingProcessId = this.form.building_process_id;
                }
                const materials = await this.$store.dispatch('requests_getItemCatalog', catalogRequest);

                // Проверяем, что materials существует и является массивом
                if (materials && Array.isArray(materials)) {
                    this.availableMaterials = materials.map(material => this.normalizeAvailableCatalogMaterial(material));
                } else {
                    console.warn('Материалы не найдены или не являются массивом:', materials);
                    this.availableMaterials = [];
                }
                this.loadedItemCatalogKey = catalogKey;
            } catch (error) {
                console.error('Ошибка загрузки справочника:', error);
                this.$bvToast.toast('Ошибка загрузки справочника', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                this.availableMaterials = [];
            } finally {
                this.materialsLoading = false;
            }
        },
        normalizeAvailableCatalogMaterial(material) {
            return {
                ...material,
                id: material.id,
                name: material.name,
                unit_of_measurement: material.unit_of_measurement || material.measures || (this.isEmployeeCatalog ? 'чел.' : (this.isFuelCardCatalog ? 'карта' : (this.isMechanicAssetCatalog ? 'ед.' : 'шт'))),
                catalog_type: this.itemCatalogType,
                catalog_name: material.catalog_name || material.catalog_group_name || null,
                accounting_type: material.accounting_type || null,
                available_quantity: material.available_quantity === undefined ? material.quantity : material.available_quantity,
                inventory_number: material.inventory_number || null,
                fuel_limits: this.cloneFuelLimits(material.fuel_limits),
                current_fuel_limits: this.cloneFuelLimits(material.fuel_limits),
                selected: false
            };
        },
        mergeAvailableCatalogMaterials(materials) {
            const byId = new Map((this.availableMaterials || [])
                .map(material => [Number(material.id), material])
                .filter(([id]) => Number.isInteger(id) && id > 0));

            (Array.isArray(materials) ? materials : [])
                .map(material => this.normalizeAvailableCatalogMaterial(material))
                .forEach(material => {
                    const id = Number(material.id);
                    if (!Number.isInteger(id) || id <= 0) {
                        return;
                    }
                    byId.set(id, {
                        ...(byId.get(id) || {}),
                        ...material
                    });
                });

            this.availableMaterials = Array.from(byId.values());
        },
        normalizeCatalogItemName(value) {
            return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
        },
        getStableCustomItemId(material, index, prefix = 'custom') {
            const currentId = material && material.id ? String(material.id) : '';
            if (currentId && currentId.indexOf('custom_') === 0) {
                return currentId;
            }

            return `${prefix}_${currentId || index || Date.now()}`;
        },
        findAvailableCatalogItemByName(name) {
            const normalizedName = this.normalizeCatalogItemName(name);
            if (!normalizedName) {
                return null;
            }

            return (this.availableMaterials || []).find(item =>
                this.normalizeCatalogItemName(item.name) === normalizedName
            ) || null;
        },
        findAvailableEmployeeCatalogItem(material) {
            const normalizedName = this.normalizeCatalogItemName(material.name || material.item_name || '');
            const numericId = Number(material.id);
            const idMatch = (this.availableMaterials || []).find(item =>
                Number(item.id) === numericId
            );

            if (idMatch && (!normalizedName || this.normalizeCatalogItemName(idMatch.name) === normalizedName)) {
                return idMatch;
            }

            return this.findAvailableCatalogItemByName(normalizedName);
        },
        normalizeEmployeeSelectedMaterial(material, index) {
            const name = material.name || material.item_name || '';
            const matchedCatalogItem = material.isCustom === true
                ? null
                : this.findAvailableEmployeeCatalogItem(material);
            const isCustom = !matchedCatalogItem;

            return {
                ...material,
                id: matchedCatalogItem ? matchedCatalogItem.id : this.getStableCustomItemId(material, index, 'custom_employee'),
                name: matchedCatalogItem ? matchedCatalogItem.name : name,
                item_name: matchedCatalogItem ? matchedCatalogItem.name : name,
                quantity: 1,
                unit_of_measurement: 'чел.',
                unit: 'чел.',
                catalog_type: this.itemCatalogType,
                isCustom
            };
        },
        buildFuelCardIssueMaterial(source = null) {
            const targetUserId = Number(this.fuelCardRecipientUserId);
            const targetUser = this.getUserById(targetUserId);
            const targetName = targetUser ? this.formatUserName(targetUser) : `Пользователь #${targetUserId}`;
            const fuelLimits = this.getInitialFuelLimits(source && source.fuel_limits);

            return {
                id: `fuel_card_recipient_${targetUserId || Date.now()}`,
                name: `Получатель: ${targetName}`,
                item_name: `Получатель: ${targetName}`,
                quantity: 1,
                unit_of_measurement: 'карта',
                unit: 'карта',
                catalog_type: 'fuel_cards',
                catalog_item_id: null,
                target_user_id: targetUserId || null,
                fuel_limits: fuelLimits,
                current_fuel_limits: [],
                isCustom: false
            };
        },
        isMechanicQuantityMaterial(material) {
            if (!this.isMechanicAssetCatalog || !material) {
                return false;
            }
            const accountingType = material.accounting_type;
            return accountingType === 'quantity' ||
                material.isCustom === true ||
                material.is_custom === true ||
                !material.catalog_item_id;
        },
        getMechanicQuantityUnit(material) {
            return String(
                (material && (material.unit_of_measurement || material.unit)) ||
                'ед.'
            ).trim() || 'ед.';
        },
        getMechanicDefaultTargetUserId() {
            return this.toPositiveInteger(this.mechanicDefaultTargetUserId);
        },
        getMechanicMaterialTargetUserId(material) {
            return this.toPositiveInteger(material && material.target_user_id) ||
                this.getMechanicDefaultTargetUserId() ||
                null;
        },
        setMechanicMaterialTargetUser(material, value) {
            if (!material) {
                return;
            }
            this.$set(material, 'target_user_id', this.toPositiveInteger(value));
        },
        buildMechanicAssetMaterial(material) {
            const rawCatalogItemId = material.catalog_item_id || material.id;
            const numericCatalogItemId = Number(rawCatalogItemId);
            const hasCatalogItemId = Number.isInteger(numericCatalogItemId) && numericCatalogItemId > 0;
            const isCustom = material.isCustom === true || !hasCatalogItemId;
            const quantity = Number(material.quantity);
            const unitText = String(material.unit || material.unit_of_measurement || '').trim().toLowerCase();
            const looksLikeQuantity = (Number.isFinite(quantity) && quantity !== 1) ||
                (unitText && unitText !== 'ед.' && unitText !== 'ед');
            const accountingType = material.accounting_type ||
                (looksLikeQuantity ? 'quantity' : 'inventory');
            const isQuantity = accountingType === 'quantity';
            const unit = material.unit_of_measurement || material.unit || (isQuantity ? 'ед.' : 'ед.');
            return {
                ...material,
                quantity: isQuantity ? (material.quantity || 1) : 1,
                unit_of_measurement: unit,
                unit,
                catalog_type: 'mechanic_assets',
                catalog_item_id: hasCatalogItemId ? numericCatalogItemId : null,
                catalog_name: material.catalog_name || null,
                accounting_type: accountingType,
                available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
                inventory_number: material.inventory_number || null,
                target_user_id: this.getMechanicMaterialTargetUserId(material) || null,
                target_building_process_id: this.mechanicAssetObjectHandledOutsidePositionCreate
                    ? null
                    : (material.target_building_process_id || null),
                target_warehouse_id: material.target_warehouse_id || null,
                meter_type: material.meter_type || material.current_meter_type || null,
                meter_value: material.meter_value === undefined ? null : material.meter_value,
                condition: material.condition || '',
                notes: material.notes || '',
                ppe_data: material.ppe_data || null,
                ppe_source_item_id: material.ppe_source_item_id || null,
                rental_counterparty: material.rental_counterparty || '',
                rental_operator_user_id: material.rental_operator_user_id || null,
                rental_expected_return_at: material.rental_expected_return_at || null,
                isCustom
            };
        },
        async normalizeSelectedMaterialsForCurrentCatalog(materials) {
            const selectedMaterials = Array.isArray(materials) ? materials : [];

            if (this.itemCatalogType !== 'employees') {
                if (this.itemCatalogType === 'fuel_cards') {
                    if (this.isFuelCardIssueToRecipient) {
                        return this.fuelCardRecipientUserId
                            ? [this.buildFuelCardIssueMaterial(selectedMaterials[0] || null)]
                            : [];
                    }
                    return selectedMaterials.map(material => ({
                        ...material,
                        quantity: 1,
                        unit_of_measurement: 'карта',
                        unit: 'карта',
                        catalog_type: 'fuel_cards',
                        catalog_item_id: material.catalog_item_id || material.id,
                        target_user_id: material.target_user_id || null,
                        fuel_limits: this.fuelCardCanEditLimits
                            ? this.sanitizeFuelLimits(material.fuel_limits)
                            : [],
                        current_fuel_limits: this.cloneFuelLimits(material.current_fuel_limits || material.fuel_limits),
                        isCustom: false
                    }));
                }
                if (this.itemCatalogType === 'mechanic_assets') {
                    return selectedMaterials.map(material => this.buildMechanicAssetMaterial(material));
                }
                return selectedMaterials;
            }

            await this.loadMaterials();
            return selectedMaterials.map((material, index) =>
                this.normalizeEmployeeSelectedMaterial(material, index)
            );
        },
        async buildSelectedMaterialsFromSourceItems(items) {
            const sourceItems = Array.isArray(items) ? items : [];
            if (this.itemCatalogType === 'employees') {
                await this.loadMaterials();
            }

            return sourceItems.map((item, index) => {
                const sourceQuantity = Number(item.quantity);
                const sourceUnit = String(item.unit || '').trim().toLowerCase();
                const sourceLooksQuantity = (Number.isFinite(sourceQuantity) && sourceQuantity !== 1) ||
                    (sourceUnit && sourceUnit !== 'ед.' && sourceUnit !== 'ед');
                const mechanicAccountingType = item.accounting_type ||
                    (sourceLooksQuantity ? 'quantity' : null);
                const isMechanicQuantity = this.itemCatalogType === 'mechanic_assets' &&
                    mechanicAccountingType === 'quantity';
                const isSingleUnitCatalog = this.itemCatalogType === 'employees' ||
                    this.itemCatalogType === 'fuel_cards' ||
                    (this.itemCatalogType === 'mechanic_assets' && !isMechanicQuantity);
                const defaultUnit = this.itemCatalogType === 'employees'
                    ? 'чел.'
                    : (this.itemCatalogType === 'fuel_cards'
                        ? 'карта'
                        : (this.itemCatalogType === 'mechanic_assets' ? 'ед.' : 'шт'));
                const baseMaterial = {
                    id: isSingleUnitCatalog
                        ? (item.catalog_item_id || item.id)
                        : `copy_item_${item.id || index}`,
                    name: item.item_name || '',
                    item_name: item.item_name || '',
                    quantity: isSingleUnitCatalog ? 1 : (item.quantity || 0),
                    unit_of_measurement: item.unit || defaultUnit,
                    unit: item.unit || defaultUnit,
                    notes: item.notes || '',
                    ppe_data: copyPpeSnapshot(item.ppe_data),
                    ppe_source_item_id: item.id,
                    catalog_type: this.itemCatalogType,
                    catalog_item_id: item.catalog_item_id || null,
                    target_user_id: item.target_user_id || null,
                    target_building_process_id: item.target_building_process_id || null,
                    target_warehouse_id: item.target_warehouse_id || null,
                    catalog_name: item.catalog_name || null,
                    accounting_type: mechanicAccountingType,
                    available_quantity: item.available_quantity === undefined ? null : item.available_quantity,
                    inventory_number: item.inventory_number || null,
                    meter_type: item.meter_type || null,
                    meter_value: item.meter_value === undefined ? null : item.meter_value,
                    condition: item.condition || '',
                    rental_counterparty: item.rental_counterparty || '',
                    rental_operator_user_id: item.rental_operator_user_id || null,
                    rental_expected_return_at: item.rental_expected_return_at || null,
                    isCustom: item.is_custom === true
                };

                if (this.itemCatalogType === 'fuel_cards') {
                    if (this.isFuelCardIssueToRecipient && item.target_user_id) {
                        this.fuelCardRecipientUserId = Number(item.target_user_id);
                        return {
                            ...baseMaterial,
                            id: `fuel_card_recipient_${item.target_user_id}`,
                            catalog_item_id: null,
                            target_user_id: item.target_user_id,
                            fuel_limits: this.getInitialFuelLimits(item.fuel_limits),
                            current_fuel_limits: [],
                            isCustom: false
                        };
                    }
                    return {
                        ...baseMaterial,
                        id: item.catalog_item_id || item.id,
                        catalog_item_id: item.catalog_item_id || item.id,
                        target_user_id: item.target_user_id || null,
                        fuel_limits: this.cloneFuelLimits(item.fuel_limits),
                        current_fuel_limits: this.cloneFuelLimits(item.fuel_limits),
                        isCustom: false
                    };
                }

                return this.itemCatalogType === 'employees'
                    ? this.normalizeEmployeeSelectedMaterial(baseMaterial, index)
                    : this.itemCatalogType === 'mechanic_assets'
                    ? this.buildMechanicAssetMaterial(baseMaterial)
                    : baseMaterial;
            });
        },
        removeMaterial(index) {
            this.form.selectedMaterials.splice(index, 1);
        },
        /**
         * Обработчик обновления материала из компонента MaterialsList
         */
        onMaterialUpdate({ index, field, value }) {
            const material = this.form.selectedMaterials[index];
            if (material) {
                // Обновляем поле материала (поддержка разных форматов)
                if (field === 'name' || field === 'item_name') {
                    this.$set(material, 'name', value);
                    this.$set(material, 'item_name', value);
                } else if (field === 'quantity') {
                    material.quantity = value;
                } else if (field === 'notes') {
                    material.notes = value;
                } else if (field === 'unit' || field === 'unit_of_measurement') {
                    material.unit_of_measurement = value;
                }
            }
        },
        addSelectedMaterials() {
            const previousCount = this.form.selectedMaterials.length;
            const selected = this.availableMaterials.filter(m => m.selected);
            selected.forEach(material => {
                const exists = !this.isMechanicAssetCatalog && this.form.selectedMaterials.find(m =>
                    m.id === material.id && (m.isCustom === false || m.isCustom === undefined)
                );
                if (!exists) {
                    const accountingType = material.accounting_type || (this.isMechanicAssetCatalog ? 'inventory' : null);
                    this.form.selectedMaterials.push({
                        ...material,
                        quantity: (this.isEmployeeCatalog || this.isFuelCardCatalog || this.isMechanicAssetCatalog) ? 1 : 0,
                        unit_of_measurement: material.unit_of_measurement || (this.isMechanicAssetCatalog ? 'ед.' : material.unit_of_measurement),
                        unit: material.unit || material.unit_of_measurement || (this.isMechanicAssetCatalog ? 'ед.' : material.unit),
                        notes: '',
                        catalog_type: this.itemCatalogType,
                        catalog_item_id: material.id,
                        catalog_name: material.catalog_name || null,
                        accounting_type: accountingType,
                        available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
                        inventory_number: material.inventory_number || null,
                        target_user_id: this.mechanicAssetNeedsTargetUser
                            ? this.getMechanicDefaultTargetUserId()
                            : (this.fuelCardNeedsRecipient ? null : undefined),
                        target_building_process_id: this.isMechanicAssetCatalog && this.mechanicAssetObjectHandledOutsidePositionCreate
                            ? null
                            : undefined,
                        target_warehouse_id: this.isMechanicAssetCatalog && this.mechanicAssetNeedsWarehouse ? null : undefined,
                        meter_type: this.isMechanicAssetCatalog ? (material.current_meter_type || null) : undefined,
                        meter_value: this.isMechanicAssetCatalog ? null : undefined,
                        condition: this.isMechanicAssetCatalog ? '' : undefined,
                        rental_counterparty: this.isMechanicAssetCatalog ? '' : undefined,
                        rental_operator_user_id: this.isMechanicAssetCatalog ? null : undefined,
                        rental_expected_return_at: this.isMechanicAssetCatalog ? null : undefined,
                        fuel_limits: this.fuelCardCanEditLimits
                            ? this.getInitialFuelLimits(material.fuel_limits)
                            : [],
                        current_fuel_limits: this.cloneFuelLimits(material.current_fuel_limits || material.fuel_limits),
                        isCustom: false // Явно указываем, что это материал из справочника
                    });
                }
            });
            this.clearMaterialSelection();
            if (this.$refs.ppePlanner) this.$refs.ppePlanner.prepareAddedItems(this.form.selectedMaterials.slice(previousCount));
            if (this.isMechanicAssetCatalog && this.mechanicAssetNeedsTargetUser && this.getMechanicDefaultTargetUserId()) {
                this.applyMechanicDefaultTargetUserToMaterials(this.getMechanicDefaultTargetUserId());
            }
        },
        getMechanicMaterialCatalogItemId(material) {
            if (!material || material.isCustom === true) {
                return null;
            }
            return this.toPositiveInteger(material.catalog_item_id || material.id);
        },
        isMechanicMaterialUsedMoreThanOnce(material) {
            const catalogItemId = this.getMechanicMaterialCatalogItemId(material);
            if (!this.isMechanicAssetCatalog || !catalogItemId) {
                return false;
            }
            const materials = Array.isArray(this.form.selectedMaterials)
                ? this.form.selectedMaterials
                : [];
            const usageCount = materials.reduce((count, item) =>
                count + (this.getMechanicMaterialCatalogItemId(item) === catalogItemId ? 1 : 0), 0);
            return usageCount > 1;
        },
        async applyMechanicDefaultTargetUser(value) {
            if (this.suppressMechanicDefaultTargetUserApply) {
                this.suppressMechanicDefaultTargetUserApply = false;
                return;
            }
            if (!this.isMechanicAssetCatalog || !this.mechanicAssetNeedsTargetUser) {
                return;
            }

            const previousValue = this.lastMechanicDefaultTargetUserId || null;
            const targetUserId = this.toPositiveInteger(value);
            const materials = Array.isArray(this.form.selectedMaterials)
                ? this.form.selectedMaterials
                : [];
            const overwritten = materials.filter(material => {
                const currentUserId = this.toPositiveInteger(material.target_user_id);
                return currentUserId && currentUserId !== targetUserId;
            });

            if (overwritten.length > 0) {
                const targetName = targetUserId
                    ? (this.getUserNameById(targetUserId) || `пользователя #${targetUserId}`)
                    : 'пустое значение';
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `На ${overwritten.length} ${this.getPositionsText(overwritten.length)} уже выбран другой МОЛ. Заменить его на ${targetName} во всех позициях?`,
                    { contentClass: 'argus-glass-dialog',
                        title: 'Изменить МОЛ во всех позициях?',
                        okTitle: 'Да, заменить',
                        cancelTitle: 'Отмена',
                        okVariant: 'warning',
                        cancelVariant: 'secondary',
                        centered: true
                    }
                );
                if (!confirmed) {
                    this.suppressMechanicDefaultTargetUserApply = true;
                    this.mechanicDefaultTargetUserId = previousValue;
                    return;
                }
            }

            this.mechanicDefaultTargetUserId = targetUserId;
            this.lastMechanicDefaultTargetUserId = targetUserId;
            this.applyMechanicDefaultTargetUserToMaterials(targetUserId);
        },
        async applyMechanicDefaultTargetUserToSelf() {
            const currentUserId = this.mechanicCurrentUserId;
            if (!currentUserId) {
                return;
            }
            await this.applyMechanicDefaultTargetUser(currentUserId);
        },
        applyMechanicDefaultTargetUserToMaterials(targetUserId) {
            const normalizedUserId = this.toPositiveInteger(targetUserId);
            const materials = Array.isArray(this.form.selectedMaterials)
                ? this.form.selectedMaterials
                : [];
            materials.forEach(material => {
                this.$set(material, 'target_user_id', normalizedUserId || null);
            });
        },
        clearMaterialSelection() {
            this.availableMaterials.forEach(material => {
                material.selected = false;
            });
            this.materialSearch = '';
        },
        async openMaterialSelector() {
            if (!this.canFillRequestDetails) {
                this.showRequestDetailsLockedToast();
                return;
            }

            if (this.isMechanicAssetCatalog) {
                this.showMechanicMaterialSelector = true;
                return;
            }

            this.showMaterialSelector = true;
            await this.loadMaterials();
        },
        addMechanicCatalogPickerMaterials(materials) {
            const selectedIds = new Set((Array.isArray(materials) ? materials : [])
                .map(material => this.toPositiveInteger(material && material.id))
                .filter(Boolean));
            if (!selectedIds.size) {
                return;
            }

            this.mergeAvailableCatalogMaterials(materials);
            this.availableMaterials.forEach(material => {
                this.$set(material, 'selected', selectedIds.has(this.toPositiveInteger(material && material.id)));
            });
            this.addSelectedMaterials();
        },
        openCustomMaterialModal() {
            if (!this.canFillRequestDetails) {
                this.showRequestDetailsLockedToast();
                return;
            }
            if (!this.canAddCustomCatalogItem) {
                return;
            }

            this.showAddCustomMaterial = true;
        },
        showRequestDetailsLockedToast() {
            const message = this.requiresBuildingProcess
                ? 'Сначала выберите срок выполнения и строительный процесс'
                : 'Сначала выберите срок выполнения';

            this.$bvToast.toast(message, {
                title: 'Заполните обязательные поля',
                variant: 'warning',
                solid: true
            });
        },
        addCustomMaterial(bvModalEvt) {
            if (bvModalEvt) {
                bvModalEvt.preventDefault();
            }

            if (!this.canFillRequestDetails) {
                this.showRequestDetailsLockedToast();
                return;
            }
            if (!this.canAddCustomCatalogItem) {
                return;
            }

            // Валидация
            if (!this.customMaterial.name.trim() ||
                (this.showCustomQuantityFields && (
                !this.customMaterial.unit_of_measurement.trim() ||
                this.customMaterial.quantity <= 0))) {
                this.$bvToast.toast('Заполните все поля', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            
            // Добавляем материал в список (с флагом isCustom)
            this.form.selectedMaterials.push({
                id: 'custom_' + Date.now(), // Уникальный ID для кастомного материала
                name: this.customMaterial.name,
                quantity: this.isEmployeeCatalog ? 1 : this.customMaterial.quantity,
                unit_of_measurement: this.isEmployeeCatalog
                    ? 'чел.'
                    : (this.customMaterial.unit_of_measurement || (this.isMechanicAssetCatalog ? 'ед.' : '')),
                unit: this.isEmployeeCatalog
                    ? 'чел.'
                    : (this.customMaterial.unit_of_measurement || (this.isMechanicAssetCatalog ? 'ед.' : '')),
                notes: this.customMaterial.notes || '',
                catalog_type: this.itemCatalogType,
                catalog_item_id: null,
                accounting_type: this.isMechanicAssetCatalog ? 'quantity' : null,
                target_user_id: this.isMechanicAssetCatalog && this.mechanicAssetNeedsTargetUser
                    ? this.getMechanicDefaultTargetUserId()
                    : undefined,
                target_building_process_id: this.isMechanicAssetCatalog && this.mechanicAssetObjectHandledOutsidePositionCreate
                    ? null
                    : undefined,
                target_warehouse_id: this.isMechanicAssetCatalog && this.mechanicAssetNeedsWarehouse ? null : undefined,
                meter_type: this.isMechanicAssetCatalog ? null : undefined,
                meter_value: this.isMechanicAssetCatalog ? null : undefined,
                condition: this.isMechanicAssetCatalog ? '' : undefined,
                rental_counterparty: this.isMechanicAssetCatalog ? '' : undefined,
                rental_operator_user_id: this.isMechanicAssetCatalog ? null : undefined,
                rental_expected_return_at: this.isMechanicAssetCatalog ? null : undefined,
                isCustom: true // Флаг что материал добавлен вручную
            });
            if (this.$refs.ppePlanner) this.$refs.ppePlanner.prepareAddedItems(this.form.selectedMaterials.slice(-1));

            if (this.isMechanicAssetCatalog && this.mechanicAssetNeedsTargetUser && this.getMechanicDefaultTargetUserId()) {
                this.applyMechanicDefaultTargetUserToMaterials(this.getMechanicDefaultTargetUserId());
            }
            
            // Закрываем модалку
            this.$nextTick(() => {
                this.showAddCustomMaterial = false;
            });
        },
        resetCustomMaterialForm() {
            this.customMaterial = {
                name: '',
                quantity: (this.isEmployeeCatalog || this.isFuelCardCatalog || this.isMechanicAssetCatalog) ? 1 : 0,
                unit_of_measurement: this.isEmployeeCatalog
                    ? 'чел.'
                    : (this.isFuelCardCatalog ? 'карта' : (this.isMechanicAssetCatalog ? 'ед.' : '')),
                notes: ''
            };
        },
        async saveDraft() {
            // Проверка флага загрузки
            if (this.savingDraft || this.submittingRequest) return;
            await this.flushPendingInputChanges();

            // Подтверждение действия
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Вы уверены, что хотите сохранить заявку как черновик? Заявка будет сохранена, но не отправлена на согласование. Вы сможете вернуться к ней позже для редактирования и отправки.',
                { contentClass: 'argus-glass-dialog',
                    title: 'Сохранение черновика',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Да, сохранить',
                    cancelTitle: 'Отмена',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            );

            if (!confirmed) return;

            this.savingDraft = true;
            try {
                await this.flushPendingInputChanges();
                this.form.selectedMaterials = await this.normalizeSelectedMaterialsForCurrentCatalog(this.form.selectedMaterials);
                const payload = this.buildCreateRequestPayload({ status: 'draft' });
                const data = await this.createRequestFromPayload(payload);
                
                // Удаляем автосохраненный черновик после успешного сохранения как заявка-черновик
                if (this.form.request_type_id) {
                    try {
                        await this.$store.dispatch('requests_deleteDraftByType', this.form.request_type_id);
                    } catch (e) {
                        console.error('Ошибка удаления автосохраненного черновика:', e);
                        // Не показываем ошибку пользователю, так как заявка уже сохранена
                    }
                }
                
                this.$bvToast.toast('Черновик сохранен', { title: 'Успех', variant: 'success', solid: true });
                notifyInstallPromotionSuccess({source: 'request-draft-saved'});
                this.$router.push(`/requests/${data.id || ''}`);
            } catch (e) {
                console.error('Ошибка сохранения черновика:', e);
                this.$bvToast.toast('Не удалось сохранить черновик', { title: 'Ошибка', variant: 'danger', solid: true });
            } finally {
                this.savingDraft = false;
            }
        },
        getDaysText(days) {
            if (days === 1) return 'день';
            if (days >= 2 && days <= 4) return 'дня';
            return 'дней';
        },
        getPositionsText(count) {
            const value = Math.abs(Number(count) || 0);
            const lastTwo = value % 100;
            const last = value % 10;
            if (lastTwo >= 11 && lastTwo <= 14) return 'позициях';
            if (last === 1) return 'позиции';
            return 'позициях';
        },
        getUserId(user) {
            return user ? (user.user_id || user.id) : null;
        },
        isActiveHumanUser(user) {
            if (!user) {
                return false;
            }
            const active = user.active;
            const isActive = active === true || active === 1 || active === '1' || active === 'true';
            const isBot = user.isBot === true || user.isBot === 1 || user.isBot === '1' || user.isBot === 'true';
            return isActive && !isBot;
        },
        formatUserName(user) {
            if (!user) return '';
            return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.full_name || `Пользователь #${this.getUserId(user)}`;
        },
        getUserById(userId) {
            const numericUserId = Number(userId);
            return (this.$store.state.users || []).find(user => Number(this.getUserId(user)) === numericUserId && this.isActiveHumanUser(user));
        },
        getUserNameById(userId) {
            const user = this.getUserById(userId);
            return user ? this.formatUserName(user) : '';
        },
        getDepartmentUserNames(departmentId) {
            const numericDepartmentId = Number(departmentId);
            const department = (this.$store.state.departments || []).find(row => Number(row.id || row.department_id) === numericDepartmentId);
            return (department && department.users || [])
                .map(user => this.formatUserName(user))
                .filter(Boolean);
        },
        getObjectRoleUserNames(roles) {
            if (!this.selectedBuildingProcess) {
                return [];
            }

            const userIds = [];
            roles.forEach(role => {
                if (role === 'curator' && this.selectedBuildingProcess.curator_id) {
                    userIds.push(this.selectedBuildingProcess.curator_id);
                } else if (role === 'main_staff' && this.selectedBuildingProcess.main_staff_id) {
                    userIds.push(this.selectedBuildingProcess.main_staff_id);
                } else if (role === 'has_access' && Array.isArray(this.selectedBuildingProcess.has_access)) {
                    userIds.push(...this.selectedBuildingProcess.has_access);
                }
            });

            return [...new Set(userIds.map(id => Number(id)).filter(Boolean))]
                .map(userId => this.getUserNameById(userId))
                .filter(Boolean);
        },
        formatUserList(userNames) {
            if (userNames.length === 0) return '';
            if (userNames.length === 1) return userNames[0];
            if (userNames.length === 2) return `${userNames[0]} и ${userNames[1]}`;
            // Для трех и более: "Иван, Петр, и Сидор"
            const last = userNames[userNames.length - 1];
            const rest = userNames.slice(0, -1);
            return `${rest.join(', ')}, и ${last}`;
        },
        formatApproversList(approvers) {
            if (approvers.length === 0) return '';
            if (approvers.length === 1) return approvers[0];
            if (approvers.length === 2) return `${approvers[0]} и ${approvers[1]}`;
            // Для трех и более: "Иван, Петр, и Сидор"
            const last = approvers[approvers.length - 1];
            const rest = approvers.slice(0, -1);
            return `${rest.join(', ')}, и ${last}`;
        },
        async submitRequest() {
            this.submissionError = '';
            if (!this.form.recurrence) {
                const policy = deadlinePolicy(this.selectedRequestType || {}, this.form);
                if (policy.code) { this.submissionError = policy.message; return; }
            }
            // Проверка флага загрузки
            if (this.savingDraft || this.submittingRequest) return;
            if (this.isReportAnnulment && !await this.$bvModal.msgBoxConfirm('После окончательного согласования выбранные отчёты будут аннулированы и исключены из расчётов. Если вы единственный согласующий, это произойдёт сразу при отправке. Отменить аннулирование через интерфейс нельзя. Продолжить?', { contentClass: 'argus-glass-dialog',okTitle: 'Отправить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true})) return;
            await this.flushPendingInputChanges();

            // Валидация названия заявки (если требуется)
            if (this.selectedRequestType && this.selectedRequestType.requires_title && !this.form.title.trim()) {
                this.$bvToast.toast('Введите название заявки', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.requiresBuildingProcess && !this.form.building_process_id) {
                this.$bvToast.toast('Выберите строительный процесс', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            // Валидация для заявок со справочником позиций
            if (this.hasItemCatalog && !this.isItemCatalogSelectionValid) {
                this.$bvToast.toast(this.itemCatalogValidationText, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.isDescriptionRequired && !this.form.description.trim()) {
                this.$bvToast.toast('Введите описание заявки', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (!this.requiredAttachmentsValidForCreate) {
                const missing = this.requiredAttachmentRulesForCreate
                    .filter(rule => this.getRequiredAttachmentCount(rule) < rule.minCount)
                    .map(rule => rule.label)
                    .join(', ');
                this.$bvToast.toast(`Приложите обязательные документы: ${missing}`, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            // Валидация для срочных заявок
            if (this.showUrgencyComment && !this.form.urgency_comment.trim()) {
                this.$bvToast.toast('Укажите причину срочности', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            const missingCustomField = this.visibleCustomFieldDefinitions.find(field =>
                field.required === true && field.type !== 'checkbox' &&
                this.isCustomFieldValueEmpty(this.form.custom_fields ? this.form.custom_fields[field.key] : undefined)
            );
            if (missingCustomField) {
                this.$bvToast.toast(`Заполните поле "${missingCustomField.label}"`, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            // Подтверждение действия
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Вы уверены, что хотите создать и отправить заявку? Заявка будет отправлена на согласование согласно настроенному процессу. После отправки редактирование будет ограничено.',
                { contentClass: 'argus-glass-dialog',
                    title: 'Создание и отправка заявки',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Да, отправить',
                    cancelTitle: 'Отмена',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            );

            if (!confirmed) return;
            await this.flushPendingInputChanges();

            // Определяем нужно ли добавлять дополнительных согласующих
            // Условия: нарушение дедлайна И требуется обоснование И включено дополнительное согласование
            const needsChiefEngineer = this.isDeadlineViolated && 
                this.selectedRequestType && 
                this.selectedRequestType.requires_justification_if_late &&
                this.selectedRequestType.late_approval_config &&
                this.selectedRequestType.late_approval_config.enabled === true;
            
            this.form.selectedMaterials = await this.normalizeSelectedMaterialsForCurrentCatalog(this.form.selectedMaterials);

            this.submittingRequest = true;
            try {
                const requestData = this.buildCreateRequestPayload({
                    status: 'submitted',
                    requires_chief_engineer: needsChiefEngineer
                });
                const data = await this.createRequestFromPayload(requestData);
                
                // Обновляем счетчики заявок после создания
                try {
                    await this.$store.dispatch('requests_getCounts');
                } catch (e) {
                    console.error('Ошибка при обновлении счетчиков:', e);
                }
                
                // Удаляем черновик после успешного создания заявки
                if (this.form.request_type_id) {
                    try {
                        await this.$store.dispatch('requests_deleteDraftByType', this.form.request_type_id);
                    } catch (e) {
                        console.error('Ошибка удаления черновика:', e);
                        // Не показываем ошибку пользователю, так как заявка уже создана
                    }
                }
                
                this.$bvToast.toast('Заявка создана', { title: 'Успех', variant: 'success', solid: true });
                notifyInstallPromotionSuccess({source: 'request-created'});
                this.$router.push(`/requests/${data.id || ''}`);
            } catch (e) {
                console.error('Ошибка создания заявки:', e);
                this.submissionError = (e.response && e.response.data && e.response.data.message) || e.message || 'Не удалось создать заявку';
                this.$bvToast.toast(e.response && e.response.data && e.response.data.message || e.message || 'Не удалось создать заявку', { title: 'Ошибка', variant: 'danger', solid: true });
            } finally {
                this.submittingRequest = false;
            }
        },
        /**
         * Автосохранение черновика с debounce
         */
        autoSaveDraft() {
            if (this.isLinkedRequestFlow || this.hasLinkedRequestQuery()) {
                return;
            }

            // Очищаем предыдущий таймер
            if (this.autoSaveTimer) {
                clearTimeout(this.autoSaveTimer);
            }
            
            // Проверяем валидность формы для черновика
            if (!this.form.request_type_id || !this.isFormValidForDraft) {
                return;
            }
            
            // Устанавливаем новый таймер (2 секунды задержка)
            const self = this;
            this.autoSaveTimer = setTimeout(function() {
                self.performAutoSave();
            }, 2000);
        },
        
        /**
         * Выполнить автосохранение черновика
         */
        async performAutoSave() {
            if (!this.form.request_type_id || !this.isFormValidForDraft) {
                return;
            }
            this.syncFuelLimitFormControls();
            
            try {
                const draftData = {
                    report_annulment: this.form.report_annulment,
                    request_type_id: this.form.request_type_id,
                    title: this.form.title || '',
                    description: this.form.description || '',
                    description_mention_state: this.form.description_mention_state || null,
                    priority: this.form.priority || 'normal',
                    deadline: this.form.deadline || null,
                    urgency_comment: this.form.urgency_comment || '',
                    building_process_id: this.form.building_process_id || null,
                    recurrence: this.form.recurrence || null,
                    custom_fields: this.form.custom_fields || {},
                    fuelCardRecipientUserId: this.fuelCardRecipientUserId || null,
                    requiredAttachmentChoices: this.form.requiredAttachmentChoices || {},
                    ppeRecipients: this.form.ppeRecipients || [],
                    selectedMaterials: (this.form.selectedMaterials || []).map(function(material) {
                        return {
                            id: material.id,
                            name: material.name,
                            quantity: material.quantity || 0,
                            unit_of_measurement: material.unit_of_measurement || '',
                            notes: material.notes || '',
                            ppe_data: material.ppe_data || null,
                            ppe_source_item_id: material.ppe_source_item_id || null,
                            catalog_type: material.catalog_type || '',
                            catalog_item_id: material.catalog_item_id || null,
                            catalog_name: material.catalog_name || null,
                            accounting_type: material.accounting_type || null,
                            available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
                            inventory_number: material.inventory_number || null,
                            target_user_id: material.target_user_id || null,
                            target_building_process_id: this.mechanicAssetObjectHandledOutsidePositionCreate ? null : (material.target_building_process_id || null),
                            target_warehouse_id: material.target_warehouse_id || null,
                            meter_type: material.meter_type || null,
                            meter_value: material.meter_value === undefined ? null : material.meter_value,
                            condition: material.condition || '',
                            rental_counterparty: material.rental_counterparty || '',
                            rental_operator_user_id: material.rental_operator_user_id || null,
                            rental_expected_return_at: material.rental_expected_return_at || null,
                            fuel_limits: material.fuel_limits || [],
                            current_fuel_limits: material.current_fuel_limits || [],
                            isCustom: material.isCustom || false
                        };
                    }),
                    attachments: (this.form.attachments || []).map(function(file) {
                        return {
                            name: file.name,
                            size: file.size,
                            type: file.type || ''
                        };
                    }),
                    lastSavedAt: new Date().toISOString()
                };
                
                await this.$store.dispatch('requests_saveDraft', {
                    requestTypeId: this.form.request_type_id,
                    draftData: draftData
                });
                
                this.lastAutoSaveTime = new Date();
                
                // Показываем toast об успешном автосохранении
                if (this.$bvToast) {
                    this.$bvToast.toast('Автосохранение успешно', {
                        title: 'Автосохранение',
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 2000,
                        toaster: 'b-toaster-top-right'
                    });
                }
            } catch (error) {
                console.error('Ошибка автосохранения черновика:', error);
                // Не показываем ошибку пользователю, чтобы не мешать работе
            }
        },
        
        /**
         * Проверить наличие черновика для текущего типа заявки
         */
        async checkForDraft() {
            if (!this.form.request_type_id) {
                return;
            }
            
            // Не показываем черновик, если загружаем данные из copyFrom
            if (this.$route.query.copyFrom || this.isLinkedRequestFlow || this.hasLinkedRequestQuery()) {
                return;
            }
            
            try {
                const draft = await this.$store.dispatch('requests_getDraft', this.form.request_type_id);
                
                if (draft && draft.draft_data && !this.draftRestoreShown) {
                    // Показываем модальное окно восстановления только один раз
                    this.draftRestoreShown = true;
                    this.showRestoreDraftModal = true;
                }
            } catch (error) {
                console.error('Ошибка проверки черновика:', error);
            }
        },
        
        /**
         * Восстановить черновик
         */
        async restoreDraft() {
            const draft = this.$store.state.requests.availableDraft;
            
            if (!draft || !draft.draft_data) {
                return;
            }
            
            const draftData = draft.draft_data;
            
            // Восстанавливаем поля формы
            if (draftData.title !== undefined) {
                this.form.title = draftData.title || '';
            }
            if (draftData.description !== undefined) {
                this.form.description = draftData.description || '';
                this.form.description_mention_state = draftData.description_mention_state || null;
            }
            if (draftData.priority !== undefined) {
                this.form.priority = draftData.priority || 'normal';
            }
            if (draftData.recurrence !== undefined) this.$set(this.form, 'recurrence', draftData.recurrence);
            if (draftData.deadline !== undefined) {
                this.form.deadline = draftData.deadline || null;
            }
            if (draftData.urgency_comment !== undefined) {
                this.form.urgency_comment = draftData.urgency_comment || '';
            }
            if (draftData.building_process_id !== undefined) {
                this.form.building_process_id = draftData.building_process_id || null;
            }
            if (draftData.custom_fields !== undefined &&
                draftData.custom_fields &&
                typeof draftData.custom_fields === 'object') {
                this.$set(this.form, 'custom_fields', { ...draftData.custom_fields });
            }
            if (draftData.fuelCardRecipientUserId !== undefined) {
                this.fuelCardRecipientUserId = draftData.fuelCardRecipientUserId || null;
            }
            if (draftData.requiredAttachmentChoices !== undefined &&
                draftData.requiredAttachmentChoices &&
                typeof draftData.requiredAttachmentChoices === 'object') {
                this.$set(this.form, 'requiredAttachmentChoices', { ...draftData.requiredAttachmentChoices });
                this.initializeRequiredAttachmentChoices();
            }
            if (draftData.selectedMaterials !== undefined && Array.isArray(draftData.selectedMaterials)) {
                if (draftData.report_annulment && Array.isArray(draftData.report_annulment.reports)) this.form.report_annulment = draftData.report_annulment;
                this.form.ppeRecipients = Array.isArray(draftData.ppeRecipients) ? draftData.ppeRecipients : [];
                const restoredMaterials = draftData.selectedMaterials.map(function(material) {
                    return {
                        id: material.id,
                        name: material.name,
                        item_name: material.item_name || material.name,
                        quantity: material.quantity || 0,
                        unit_of_measurement: material.unit_of_measurement || '',
                        unit: material.unit || material.unit_of_measurement || '',
                        notes: material.notes || '',
                        ppe_data: material.ppe_data || null,
                        ppe_source_item_id: material.ppe_source_item_id || null,
                        catalog_type: material.catalog_type || '',
                        catalog_item_id: material.catalog_item_id || null,
                        catalog_name: material.catalog_name || null,
                        accounting_type: material.accounting_type || null,
                        available_quantity: material.available_quantity === undefined ? null : material.available_quantity,
                        inventory_number: material.inventory_number || null,
                        target_user_id: material.target_user_id || null,
                        target_building_process_id: this.mechanicAssetObjectHandledOutsidePositionCreate ? null : (material.target_building_process_id || null),
                        target_warehouse_id: material.target_warehouse_id || null,
                        meter_type: material.meter_type || null,
                        meter_value: material.meter_value === undefined ? null : material.meter_value,
                        condition: material.condition || '',
                        rental_counterparty: material.rental_counterparty || '',
                        rental_operator_user_id: material.rental_operator_user_id || null,
                        rental_expected_return_at: material.rental_expected_return_at || null,
                        fuel_limits: material.fuel_limits || [],
                        current_fuel_limits: material.current_fuel_limits || [],
                        isCustom: material.isCustom || false
                    };
                });
                if (!this.fuelCardRecipientUserId && restoredMaterials.length > 0 && restoredMaterials[0].target_user_id) {
                    this.fuelCardRecipientUserId = restoredMaterials[0].target_user_id;
                }
                this.form.selectedMaterials = await this.normalizeSelectedMaterialsForCurrentCatalog(restoredMaterials);
            }
            
            // Примечание: файлы не восстанавливаются, так как они не сохраняются в черновике
            // Показываем уведомление пользователю
            if (draftData.attachments && draftData.attachments.length > 0) {
                this.$bvToast.toast('Файлы не были восстановлены. Пожалуйста, загрузите их заново.', {
                    title: 'Внимание',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 5000
                });
            }
            
            this.showRestoreDraftModal = false;
            
            this.$bvToast.toast('Черновик восстановлен', {
                title: 'Успех',
                variant: 'success',
                solid: true
            });
        },
        
        /**
         * Начать заново (не восстанавливать черновик)
         */
        startFresh() {
            this.showRestoreDraftModal = false;
            // Форма остается пустой
        },
        
        /**
         * Получить отформатированную дату последнего сохранения
         */
        getLastSavedDate() {
            const draft = this.$store.state.requests.availableDraft;
            if (!draft || !draft.draft_data || !draft.draft_data.lastSavedAt) {
                return null;
            }
            
            const date = new Date(draft.draft_data.lastSavedAt);
            return date.toLocaleString('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        
        // Обработчик paste для textarea описания
        onPasteToDescription(event) {
            this.handlePaste(event, (files) => {
                this.addFilesFromClipboardToDescription(files);
            });
        },
        // Обработчик кнопки вставки из буфера
        async pasteFromClipboardToDescription() {
            await this.handleClipboardButton((files) => {
                this.addFilesFromClipboardToDescription(files);
            });
        },
        async pasteFromClipboardToRequiredAttachment(rule) {
            await this.handleClipboardButton((files) => {
                this.addFilesFromClipboardToRequiredAttachment(rule, files);
            });
        },
        // Добавление файлов из буфера обмена к описанию
        addFilesFromClipboardToDescription(files) {
            const byKey = f => `${f.name}|${f.size}|${f.lastModified}`;
            const map = new Map(this.form.attachments.map(f => [byKey(f), f]));
            files.forEach(f => {
                const key = byKey(f);
                if (!map.has(key)) map.set(key, f);
            });
            this.form.attachments = Array.from(map.values());
        },
        addFilesFromClipboardToRequiredAttachment(rule, files) {
            this.onRequiredAttachmentFilesChange(rule, files);
        },

        getAvailableLinkedRule(sourceRequest, ruleKey, requestTypeId) {
            const rules = sourceRequest && Array.isArray(sourceRequest.available_linked_request_rules)
                ? sourceRequest.available_linked_request_rules
                : [];
            const normalizedRuleKey = String(ruleKey || '').trim();
            const normalizedTypeId = this.toPositiveInteger(requestTypeId);

            return rules.find(rule => {
                if (rule.key !== normalizedRuleKey) {
                    return false;
                }
                if (!normalizedTypeId) {
                    return true;
                }

                return Number(rule.targetRequestTypeId || rule.target_request_type_id) === normalizedTypeId;
            }) || null;
        },
        buildLinkedRequestTitle(rule, sourceRequest, targetRequestType) {
            const sourceNumber = sourceRequest && (sourceRequest.request_number || sourceRequest.id)
                ? String(sourceRequest.request_number || sourceRequest.id)
                : '';
            const sourceId = sourceRequest && sourceRequest.id ? String(sourceRequest.id) : '';
            const targetTypeName = targetRequestType && targetRequestType.text
                ? targetRequestType.text
                : 'Связанная заявка';
            const sourceTitle = sourceRequest && sourceRequest.title ? String(sourceRequest.title) : '';
            const sourceType = sourceRequest && sourceRequest.type_name ? String(sourceRequest.type_name) : '';
            const objectName = sourceRequest && sourceRequest.building_object_name
                ? String(sourceRequest.building_object_name)
                : '';
            const contractName = sourceRequest && sourceRequest.building_contract_name
                ? String(sourceRequest.building_contract_name)
                : '';
            const template = rule && rule.titleTemplate
                ? rule.titleTemplate
                : `${targetTypeName} по заявке {sourceNumber}`;

            return template
                .replace(/\{sourceId\}/g, sourceId)
                .replace(/\{sourceNumber\}/g, sourceNumber)
                .replace(/\{sourceTitle\}/g, sourceTitle)
                .replace(/\{sourceType\}/g, sourceType)
                .replace(/\{targetType\}/g, targetTypeName)
                .replace(/\{object\}/g, objectName)
                .replace(/\{contract\}/g, contractName)
                .trim();
        },
        ensureBuildingProcessOptionFromSource(sourceRequest) {
            const buildingProcessId = this.toPositiveInteger(sourceRequest && sourceRequest.building_process_id);
            if (!buildingProcessId) {
                return;
            }

            const exists = this.buildingProcesses.some(process =>
                Number(process.value || process.building_process_id) === buildingProcessId
            );
            if (exists) {
                return;
            }

            const objectName = sourceRequest.building_object_name || `Объект #${buildingProcessId}`;
            const contractName = sourceRequest.building_contract_name || null;
            const text = contractName ? `${objectName} (${contractName})` : objectName;
            this.buildingProcesses.unshift({
                value: buildingProcessId,
                building_process_id: buildingProcessId,
                object_name: objectName,
                contract_name: contractName,
                text
            });
        },
        async loadLinkedSourceRequest(sourceRequestId, ruleKey, requestTypeId) {
            this.loadingSourceRequest = true;
            this.linkedSourceRequestId = this.toPositiveInteger(sourceRequestId);
            this.linkedRuleKey = String(ruleKey || '').trim();

            try {
                if (!this.linkedSourceRequestId || !this.linkedRuleKey) {
                    throw new Error('Не указана исходная заявка или правило связи');
                }

                const sourceRequest = await this.$store.dispatch('requests_getRequest', this.linkedSourceRequestId);
                if (!sourceRequest) {
                    throw new Error('Исходная заявка не найдена');
                }

                const rule = this.getAvailableLinkedRule(sourceRequest, this.linkedRuleKey, requestTypeId);
                if (!rule) {
                    throw new Error('Создание связанной заявки недоступно по настройкам типа заявки');
                }

                const targetRequestTypeId = this.toPositiveInteger(
                    rule.targetRequestTypeId ||
                    rule.target_request_type_id ||
                    requestTypeId
                );
                const targetRequestType = await this.ensureRequestTypeAvailable(targetRequestTypeId);
                if (!targetRequestType) {
                    throw new Error('Целевой тип заявки не найден или недоступен для создания');
                }

                this.linkedSourceRequest = sourceRequest;
                this.linkedSourceRule = rule;
                this.form.request_type_id = targetRequestType.value;
                this.syncSelectedCategoryFromType();
                await this.onRequestTypeChange({ skipDraftCheck: true });

                this.form.title = this.buildLinkedRequestTitle(rule, sourceRequest, targetRequestType);
                this.form.description = '';
                this.form.priority = 'normal';
                this.form.deadline = null;
                this.form.urgency_comment = '';
                this.form.selectedMaterials = [];
                this.form.attachments = [];
                this.form.requiredAttachments = {};
                this.requiredAttachmentInputs = {};
                this.initializeRequiredAttachmentChoices();

                if (rule.copyBuildingProcess !== false && sourceRequest.building_process_id) {
                    this.ensureBuildingProcessOptionFromSource(sourceRequest);
                    this.form.building_process_id = this.toPositiveInteger(sourceRequest.building_process_id);
                }

                this.collapseRequestTypePicker();
                this.linkedRequestContextLoaded = true;
            } catch (error) {
                console.error('Ошибка загрузки связанной заявки:', error);

                const errorMessage = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : (error.message || 'Не удалось подготовить связанную заявку');

                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });

                this.linkedSourceRequestId = null;
                this.linkedRuleKey = null;
                this.linkedSourceRequest = null;
                this.linkedSourceRule = null;
                this.linkedRequestContextLoaded = false;
                this.$router.replace({ path: '/requests/create', query: {} });
            } finally {
                this.loadingSourceRequest = false;
            }
        },
        
        /**
         * Загрузить данные исходной заявки для копирования
         */
        async loadSourceRequest(requestId) {
            this.loadingSourceRequest = true;
            try {
                const sourceRequest = await this.$store.dispatch('requests_getRequest', requestId);
                
                if (!sourceRequest) {
                    throw new Error('Заявка не найдена');
                }
                
                // Заполняем форму данными из исходной заявки
                this.form.request_type_id = sourceRequest.request_type_id;
                this.syncSelectedCategoryFromType();
                
                // Название с префиксом "Копия: " если его еще нет
                if (sourceRequest.title) {
                    const titlePrefix = 'Копия: ';
                    if (sourceRequest.title.startsWith(titlePrefix)) {
                        this.form.title = sourceRequest.title;
                    } else {
                        this.form.title = titlePrefix + sourceRequest.title;
                    }
                }
                
                // Описание
                this.form.description = sourceRequest.description || '';

                const sourceWorkflowData = sourceRequest.workflow_data || {};
                const sourceCustomFields = sourceWorkflowData.custom_fields || sourceWorkflowData.customFields || {};
                this.$set(this.form, 'custom_fields', sourceCustomFields && typeof sourceCustomFields === 'object'
                    ? { ...sourceCustomFields }
                    : {});
                
                // Приоритет
                this.form.priority = sourceRequest.priority || 'normal';
                
                // Deadline НЕ копируется (оставляем null)
                this.form.deadline = null;
                if (this.$route.query.recurrenceSeries && sourceWorkflowData.recurrence) {
                    this.recurrenceInheritedFiles = sourceRequest.files || [];
                    this.form.title = sourceRequest.title;
                    this.form.recurrence = {seriesId: Number(this.$route.query.recurrenceSeries), expectedVersion: Number(this.$route.query.recurrenceVersion), schedule: sourceWorkflowData.recurrence.schedule};
                    this.setRecurrenceSchedule(this.form.recurrence.schedule);
                }
                
                // Обоснование срочности НЕ копируется (очищаем, так как deadline null)
                // Поле будет показано автоматически при выборе deadline, если потребуется
                this.form.urgency_comment = '';
                
                // Строительный процесс (для заявок на материалы)
                this.form.building_process_id = sourceRequest.building_process_id || null;
                
                // Преобразуем материалы из items в selectedMaterials
                if (sourceRequest.items && Array.isArray(sourceRequest.items)) {
                    const materialItems = sourceRequest.items.filter(item => item.item_type === 'material');
                    this.form.selectedMaterials = await this.buildSelectedMaterialsFromSourceItems(materialItems);
                } else {
                    this.form.selectedMaterials = [];
                }
                
                // Файлы НЕ копируются (оставляем пустой массив)
                this.form.attachments = [];
                this.form.requiredAttachments = {};
                this.$set(this.form, 'requiredAttachmentChoices', {});
                this.initializeRequiredAttachmentChoices();
                this.requiredAttachmentInputs = {};
                
                this.sourceRequestLoaded = true;
                
                // Если тип показывает строительный процесс, загружаем доступные процессы
                if (this.showsBuildingProcess) {
                    await this.loadBuildingProcesses();
                }
                
                // Проверяем, что тип заявки существует и доступен
                const requestType = this.requestTypes.find(type => type.value === this.form.request_type_id);
                if (!requestType) {
                    this.$bvToast.toast('Тип заявки не найден или недоступен. Пожалуйста, выберите другой тип.', {
                        title: 'Внимание',
                        variant: 'warning',
                        solid: true,
                        autoHideDelay: 5000
                    });
                }
                
                // Показываем уведомление
                this.$bvToast.toast('Данные заявки загружены. Вы можете отредактировать их перед созданием.', {
                    title: 'Успех',
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 4000
                });
                
            } catch (error) {
                console.error('Ошибка загрузки исходной заявки:', error);
                
                let errorMessage = 'Не удалось загрузить данные заявки';
                if (error.response && error.response.status === 404) {
                    errorMessage = 'Заявка не найдена';
                } else if (error.response && error.response.status === 403) {
                    errorMessage = 'Нет прав доступа к этой заявке';
                } else if (error.response && error.response.data && error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
                
                // Очищаем query параметр и возвращаемся к обычному созданию
                this.$router.replace({ path: '/requests/create', query: {} });
            } finally {
                this.loadingSourceRequest = false;
            }
        }
    },
    watch: {
        'form.request_type_id'() {
            this.ppePlannerActive = false; this.form.ppeRecipients = [];
            if (!this.recurrenceAvailable) this.form.recurrence = null;
        },
        'form.attachments': {
            handler(newAttachments, oldAttachments) {
                // Очищаем URL для файлов, которые больше не в массиве
                if (oldAttachments && oldAttachments.length) {
                    const currentFiles = new Set(newAttachments || []);
                    oldAttachments.forEach(file => {
                        if (!currentFiles.has(file) && this.filePreviewUrls.has(file)) {
                            const url = this.filePreviewUrls.get(file);
                            URL.revokeObjectURL(url);
                            this.filePreviewUrls.delete(file);
                        }
                    });
                }
            },
            deep: true
        },
        'form': {
            handler: function() {
                this.autoSaveDraft();
            },
            deep: true
        },
        fuelCardRecipientUserId() {
            if (this.isFuelCardIssueToRecipient) {
                const existing = Array.isArray(this.form.selectedMaterials) && this.form.selectedMaterials.length
                    ? this.form.selectedMaterials[0]
                    : null;
                this.form.selectedMaterials = this.fuelCardRecipientUserId
                    ? [this.buildFuelCardIssueMaterial(existing)]
                    : [];
            }
            this.autoSaveDraft();
        }
    },
    async mounted() {
        this.setupTelegramBackNavigation();

        // Загрузка типов заявок
        try {
            await this.$store.dispatch('requests_getRequestTypes');
            this.requestTypesLoaded = true;
            this.syncSelectedCategoryFromType();
        } catch (e) {
            console.error('Ошибка загрузки типов заявок:', e);
            this.$bvToast.toast('Не удалось загрузить типы заявок', { 
                title: 'Ошибка', 
                variant: 'danger', 
                solid: true 
            });
        }
        
        // Загрузка пользователей, если их нет в store
        if (!this.$store.state.users || this.$store.state.users.length === 0) {
            try {
                const data = await this.$store.dispatch('getUsers');
                if (data && Array.isArray(data)) {
                    const activeUsers = data.filter(user => this.isActiveHumanUser(user));
                    this.$store.commit('setUsers', activeUsers);
                }
            } catch (e) {
                console.error('Ошибка загрузки пользователей:', e);
                // Не показываем ошибку пользователю, только логируем в консоль
            }
        }
        
        // Загрузка отделов, если их нет в store
        if (!this.$store.state.departments || this.$store.state.departments.length === 0) {
            try {
                await this.$store.dispatch('getDepartments');
            } catch (e) {
                console.error('Ошибка загрузки отделов:', e);
                // Не показываем ошибку пользователю, только логируем в консоль
            }
        }

        if (!this.$store.state.fuelTypes || this.$store.state.fuelTypes.length === 0) {
            this.$store.dispatch('getTypesOfFuel').catch(e => {
                console.error('Ошибка загрузки видов топлива:', e);
            });
        }
        
        const linkedQuery = this.getLinkedRequestQuery();
        if (linkedQuery.sourceRequestId || linkedQuery.linkRuleKey) {
            await this.loadLinkedSourceRequest(
                linkedQuery.sourceRequestId,
                linkedQuery.linkRuleKey,
                linkedQuery.requestTypeId
            );
            return;
        }

        // Проверяем наличие параметра copyFrom для копирования заявки
        const copyFromId = this.$route.query.copyFrom;
        if (copyFromId) {
            await this.loadSourceRequest(copyFromId);
        } else if (!await this.initializeRequestFromQuery()) {
            // Проверяем наличие черновика если тип заявки уже выбран (например, из query параметров)
            if (this.form.request_type_id) {
                await this.checkForDraft();
            }
        }
    },
    beforeDestroy() {
        this.teardownTelegramBackNavigation();

        // Очищаем таймер автосохранения
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
        }
        
        // Освобождаем все созданные URL при размонтировании компонента
        this.filePreviewUrls.forEach((url) => {
            URL.revokeObjectURL(url);
        });
        this.filePreviewUrls.clear();
    }
}
</script>

<style scoped>
.bg-light {
    background-color: #f8f9fa !important;
}

.text-primary {
    color: #007bff !important;
}

.bg-warning.bg-opacity-10 {
    background-color: rgba(255, 193, 7, 0.1) !important;
    border-color: #ffc107 !important;
}

.text-warning {
    color: #856404 !important;
}

.gap-2 {
    gap: 0.5rem;
}

.request-picker-summary {
    overflow: hidden;
}

.request-picker-summary__button {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 0;
    background: transparent;
}

.request-step-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0.85rem;
    gap: 0.75rem;
}

.request-step-summary__content {
    min-width: 0;
}

.request-step-summary__content .font-weight-bold {
    overflow-wrap: anywhere;
}

.request-step-summary__action {
    flex: 0 0 auto;
    padding-left: 0.25rem;
    padding-right: 0;
    font-weight: 600;
}

.request-type-search {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0.55rem;
    border: 1px solid #d7dde5;
    border-radius: 0.35rem;
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(33, 37, 41, 0.05);
}

.request-picker-section {
    position: relative;
    padding: 1rem;
    border: 1px solid #dfe5ec;
    border-radius: 0.35rem;
}

.request-picker-section::before {
    content: "";
    position: absolute;
    top: 0.75rem;
    bottom: 0.75rem;
    left: 0;
    width: 4px;
    border-radius: 0 4px 4px 0;
}

.request-picker-section--category {
    background: #f6f9ff;
}

.request-picker-section--category::before {
    background: #0d6efd;
}

.request-picker-section--type {
    background: #fbfaf5;
}

.request-picker-section--type::before {
    background: #b38300;
}

.request-picker-section__header {
    margin-bottom: 0.9rem;
    padding-left: 0.35rem;
}

.request-choice-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 0.75rem;
}

.request-choice-grid--types {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.request-choice-card {
    height: 116px;
    width: 100%;
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.35rem;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.request-choice-card--category {
    border-left: 4px solid #9ec5fe;
}

.request-choice-card--type {
    border-left: 4px solid #ffd966;
}

.request-choice-card--idle {
    border-color: #dee2e6;
}

.request-choice-card--selected {
    background: #ffffff;
}

.request-choice-card--category-selected {
    border-color: #0d6efd;
    border-left-color: #0d6efd;
}

.request-choice-card--type-selected {
    border-color: #b38300;
    border-left-color: #b38300;
}

.request-choice-card:hover,
.request-choice-card:focus {
    transform: translateY(-1px);
    box-shadow: 0 0.125rem 0.35rem rgba(0, 0, 0, 0.08);
    outline: none;
}

.request-choice-card__layout {
    display: flex;
    align-items: flex-start;
    height: 100%;
    min-width: 0;
}

.request-choice-card__text {
    min-width: 0;
    overflow: hidden;
}

.request-choice-title {
    display: -webkit-box;
    overflow: hidden;
    white-space: normal;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-height: 1.25;
}

.request-choice-grid--types .request-choice-card {
    height: auto;
    min-height: 116px;
}

.request-choice-grid--types .request-choice-card__text {
    overflow: visible;
}

.request-choice-grid--types .request-choice-title {
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
}

.request-choice-icon {
    width: 1.5rem;
    flex: 0 0 1.5rem;
    font-size: 1.25rem;
    line-height: 1.5rem;
    text-align: center;
}

.request-choice-description {
    display: -webkit-box;
    overflow: hidden;
    white-space: normal;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-height: 1.25;
}

.request-details-lock {
    padding: 1rem;
}

.fuel-card-selected-item {
    background: #ffffff;
    min-width: 0;
}

.fuel-card-selected-item .font-weight-bold,
.fuel-card-selected-item small {
    overflow-wrap: anywhere;
}

.fuel-card-selected-item .pr-3 {
    min-width: 0;
}

.fuel-card-selected-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
    min-width: 0;
}

.fuel-card-selected-grid > * {
    min-width: 0;
}

.fuel-card-limits-panel {
    min-width: 0;
}

.mechanic-asset-selected-item {
    position: relative;
    margin-bottom: 0.75rem;
    padding: 0.75rem 0.85rem 0.85rem 1rem;
    border: 1px solid #d7dee8;
    border-left: 4px solid #2f80ed;
    border-radius: 0.25rem;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(18, 38, 63, 0.05);
    min-width: 0;
}

.mechanic-asset-selected-item--quantity {
    border-left-color: #20865a;
}

.mechanic-asset-selected-item--missing-mol {
    border-color: #f1c40f;
    border-left-color: #f39c12;
    background: #fffdf5;
}

.mechanic-asset-selected-header {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: 0.65rem;
    align-items: start;
    margin-bottom: 0.65rem;
}

.mechanic-asset-selected-index {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #eef3f8;
    color: #31536f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    line-height: 1;
}

.mechanic-asset-selected-main {
    min-width: 0;
}

.mechanic-asset-selected-title {
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.mechanic-asset-selected-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.6rem;
    margin-top: 0.25rem;
    color: #607286;
    font-size: 0.78rem;
    line-height: 1.25;
}

.mechanic-asset-selected-meta span {
    display: inline-flex;
    align-items: center;
    min-width: 0;
}

.mechanic-asset-selected-meta i {
    width: 0.95rem;
    margin-right: 0.22rem;
    text-align: center;
    color: #7c8da1;
}

.mechanic-asset-selected-meta .mechanic-asset-used-marker {
    padding: 0.12rem 0.35rem;
    border-radius: 0.2rem;
    background: #fff3cd;
    color: #856404;
    font-weight: 700;
}

.mechanic-asset-selected-meta .mechanic-asset-used-marker i {
    color: #856404;
}

.mechanic-asset-selected-remove {
    width: 32px;
    height: 32px;
    padding: 0;
}

.mechanic-asset-selected-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.55rem 0.75rem;
    align-items: start;
    min-width: 0;
}

.mechanic-asset-selected-grid > * {
    min-width: 0;
    margin-bottom: 0;
}

.mechanic-asset-selected-grid ::deep(.form-group) {
    margin-bottom: 0;
}

.mechanic-asset-selected-grid ::deep(label) {
    margin-bottom: 0.25rem;
    font-size: 0.78rem;
    color: #5f6f80;
    font-weight: 600;
}

.mechanic-default-target-user-control {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    min-width: 0;
}

.mechanic-default-target-user-control .v-select {
    flex: 1 1 auto;
    min-width: 0;
}

.mechanic-default-target-user-self {
    flex: 0 0 auto;
    min-height: 38px;
    white-space: nowrap;
}

.fuel-card-limit-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
}

.fuel-card-limit-row .custom-select,
.fuel-card-limit-row .form-control {
    min-width: 0;
}

.fuel-card-limit-row .btn {
    justify-self: start;
    width: 34px;
}

@media (min-width: 768px) {
    .fuel-card-selected-grid {
        grid-template-columns: minmax(220px, 0.9fr) minmax(300px, 1.1fr);
    }

    .mechanic-asset-selected-grid {
        grid-template-columns: repeat(2, minmax(190px, 1fr));
    }

    .fuel-card-limit-row {
        grid-template-columns: minmax(150px, 1fr) minmax(120px, 160px) 34px;
    }
}

@media (min-width: 992px) {
    .mechanic-asset-selected-grid {
        grid-template-columns: repeat(3, minmax(170px, 1fr));
    }
}

::deep(.request-prerequisite-grid) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
}

::deep(.request-prerequisite-card) {
    min-height: 96px;
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    background: #ffffff;
}

::deep(.request-prerequisite-card--done) {
    border-color: #28a745;
    background: #f4fbf6;
}

::deep(.request-prerequisite-card__layout) {
    display: flex;
    align-items: flex-start;
    min-width: 0;
}

@media (max-width: 575.98px) {
    .request-category-step--collapsed {
        display: none;
    }

    .request-choice-grid,
    .request-choice-grid--types {
        grid-template-columns: 1fr;
    }

    ::deep(.request-prerequisite-grid) {
        grid-template-columns: 1fr;
    }

    .request-choice-card {
        height: 108px;
    }

    .request-choice-grid--types .request-choice-card {
        height: auto;
        min-height: 108px;
    }

    .mechanic-asset-selected-item {
        margin-left: -0.25rem;
        margin-right: -0.25rem;
        padding: 0.65rem 0.65rem 0.75rem 0.8rem;
        border-left-width: 3px;
    }

    .mechanic-asset-selected-header {
        grid-template-columns: 24px minmax(0, 1fr) 30px;
        gap: 0.5rem;
        margin-bottom: 0.55rem;
    }

    .mechanic-asset-selected-index {
        width: 24px;
        height: 24px;
        font-size: 0.75rem;
    }

    .mechanic-asset-selected-remove {
        width: 30px;
        height: 30px;
    }

    .mechanic-asset-selected-meta {
        gap: 0.25rem 0.45rem;
        font-size: 0.75rem;
    }

    .mechanic-asset-selected-grid {
        gap: 0.5rem;
    }

    .mechanic-default-target-user-control {
        flex-direction: column;
    }

    .mechanic-default-target-user-self {
        width: 100%;
    }
}

/* Стили для vue-select совместимость с Bootstrap Vue */
::deep(.v-select) {
    width: 100%;
}

::deep(.v-select .vs__dropdown-toggle) {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
}

::deep(.v-select .vs__dropdown-toggle:focus-within) {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

::deep(.v-select .vs__search) {
    margin: 0;
    padding: 0.375rem 0.75rem;
}

::deep(.v-select .vs__selected-options) {
    padding: 0.375rem 0.75rem;
}

/* Стили для превью файлов */
.files-parent {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 10px;
}

.request-create-required-file-preview {
    cursor: pointer;
}

.request-create-required-file-preview:hover {
    color: #0056b3;
}

.file-container {
    width: 100px;
    text-align: center;
    position: relative;

    .remove-icon {
        position: absolute;
        right: 0;
        top: 0;
        cursor: pointer;
        z-index: 10;
        background: white;
        border-radius: 50%;
        padding: 2px 4px;
    }

    .remove-icon:hover {
        font-size: 110%;
    }

    .file-preview {
        width: 100%;
        height: 80px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #f8f9fa;
        overflow: hidden;
        cursor: pointer;
        
        &:hover {
            border-color: #007bff;
            background-color: #e9ecef;
        }
    }

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: cover;
    }

    .link-text {
        margin-top: 4px;
        
        .file-name {
            display: block;
            font-size: 0.875rem;
            text-overflow: ellipsis;
            word-wrap: break-word;
            overflow: hidden;
            max-height: 2.6em;
            line-height: 1.3em;
            color: #212529;
        }
    }
}
</style>
